'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-connect/composing-and-sending.mdx
 * (published at user-docs.totalaccess.co.za/total-connect/composing-and-sending)
 */

/**
 * MultiComposeManager - Manages multiple simultaneous email compose windows
 * 
 * Features:
 * - Up to 10 concurrent compose windows
 * - Only 1 can be open/fullscreen at a time
 * - Up to 9 can be minimized as tabs
 * - Each instance auto-saves constantly
 * - Persists drafts to localStorage and TotalAccessOfflineDB (email_drafts / offlineDb)
 * - CapacitorModalHeader chrome (matches Email mobile modal); desktop email slide-in vs centered modal
 * - Keyboard navigation for email suggestions
 * - Contact picker with company users
 * - Scheduled sending
 * - Calendar meeting invite attachments
 * - Optimistic outbox updates for instant feedback
 */

import UserDocsLink from '@/components/shared/UserDocsLink';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { Email, EmailAddress, SendEmailRequest, Attachment } from '../types';
import type { SecurityAnalysis } from './EmailSecurityBanner';
import emailApiService from '../services/emailApi.service';
import { useEmailStore } from '../stores/emailStore';
import * as notify from '@/lib/notifications';
import RichTextEditor from './RichTextEditor';
import type { RichTextEditorRef } from './RichTextEditor';

import {
  X, Send, Paperclip, Minimize2, Maximize2, ChevronDown, ChevronUp, Users, Square,
  Calendar, Clock, Video, MessageSquare, Phone, UserPlus, Search, Check,
  Building2, Globe, Zap, FileText, Image as ImageIcon, FileVideo, FileAudio,
  File as FileIcon, FileArchive, FileSpreadsheet, FileCode, Loader2,
  Plus, Pencil, Trash2, Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { authService } from '@/lib/authService';
import { fetchEmailSecuritySettings } from '@/hooks/useEmailSecuritySettings';
import type { BannerSettings } from '@/hooks/useEmailSecuritySettings';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/hooks/useAuth';
import { isCapacitor } from '@/lib/platformUtils';
import {
  hydrateEmailConnectPrefs,
  getEmailConnectPrefsSnapshot,
} from '@/lib/offline/emailConnectPreferencesStore';
import EncryptedBadge from './EncryptedBadge';
import { TotalConnectAccessGate } from '@/components/TotalConnectAccessGate';
import CapacitorModalHeader from '@/components/CapacitorModalHeader';
import EmailRecipientsInput from './EmailRecipientsInput';
import { useModalState } from '@/contexts/ModalStateContext';
import { desktopNotifications } from '@/lib/desktopNotifications';
import EmailAttachmentViewer from './EmailAttachmentViewer';
import { sanitizeEmailHtml } from '../utils/emailViewerUtils';

// Get file icon for composer attachment preview based on content type
function getComposerFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return ImageIcon;
  if (contentType.startsWith('video/')) return FileVideo;
  if (contentType.startsWith('audio/')) return FileAudio;
  if (contentType.includes('pdf')) return FileText;
  if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv')) return FileSpreadsheet;
  if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('tar') || contentType.includes('gzip')) return FileArchive;
  if (contentType.includes('javascript') || contentType.includes('json') || contentType.includes('xml') || contentType.includes('html')) return FileCode;
  if (contentType.includes('text') || contentType.includes('document') || contentType.includes('word')) return FileText;
  return FileIcon;
}

// File preview modal for new File objects in composer (uses blob URLs)
function ComposerFilePreviewModal({ file, isOpen, onClose }: { file: File | null; isOpen: boolean; onClose: () => void }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!file || !isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- revoke/teardown branch
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync blob URL to state for iframe/img
    setObjectUrl(url);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    return () => URL.revokeObjectURL(url);
  }, [file, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  if (!isOpen || !file || !objectUrl) return null;

  const isImage = file.type.startsWith('image/');
  const isPdf = file.type.includes('pdf');
  const isVideo = file.type.startsWith('video/');
  const isAudio = file.type.startsWith('audio/');

  return createPortal(
    <div
      className="fixed inset-0 bg-black flex flex-col"
      style={{ zIndex: 999999, isolation: 'isolate' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-3 text-white min-w-0">
          <span className="font-medium truncate max-w-md">{file.name}</span>
          <span className="text-sm text-gray-400 flex-shrink-0">
            {file.size < 1024 ? file.size + ' B' : file.size < 1048576 ? (file.size / 1024).toFixed(1) + ' KB' : (file.size / 1048576).toFixed(1) + ' MB'}
          </span>
        </div>
        <button onClick={onClose} className="p-2 bg-red-600/80 hover:bg-red-600 text-white rounded-lg" title="Close (Esc)">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-auto p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {loading && <Loader2 className="w-8 h-8 text-white animate-spin absolute" />}
        {isImage && <img src={objectUrl} alt={file.name} className="max-w-full max-h-full object-contain" onLoad={() => setLoading(false)} />}
        {isPdf && <iframe src={objectUrl} className="w-full h-full bg-white dark:bg-gray-950 rounded-lg" title={file.name} onLoad={() => setLoading(false)} />}
        {isVideo && <video src={objectUrl} controls className="max-w-full max-h-full" onLoadedData={() => setLoading(false)} />}
        {isAudio && <div className="bg-gray-800 p-8 rounded-xl"><audio src={objectUrl} controls className="w-80" onLoadedData={() => setLoading(false)} /></div>}
        {!isImage && !isPdf && !isVideo && !isAudio && (
          <div className="text-center text-white">
            <FileIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p>Preview not available for this file type</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Composer Attachment Preview - shows rich thumbnails for attachments in the composer
// Uses EmailAttachmentViewer for forwarded attachments (full preview + thumbnails)
// and blob URL thumbnails for new File objects
function ComposerAttachmentPreview({
  files,
  forwardedAttachments,
  onRemoveFile,
  onRemoveForwarded,
  messageId,
  defaultCollapsed,
}: {
  files: File[];
  forwardedAttachments?: Attachment[];
  onRemoveFile: (index: number) => void;
  onRemoveForwarded: (index: number) => void;
  messageId?: string;
  defaultCollapsed?: boolean;
}) {
  const [objectUrls, setObjectUrls] = useState<Record<number, string>>({});
  const [imgLoading, setImgLoading] = useState<Record<string, boolean>>({});
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false);

  // Create and revoke object URLs for File objects
  useEffect(() => {
    const urls: Record<number, string> = {};
    files.forEach((file, i) => {
      if (file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/')) {
        urls[i] = URL.createObjectURL(file);
      }
    });
    // eslint-disable-next-line react-hooks/set-state-in-effect -- derive preview URLs from `files`
    setObjectUrls(urls);
    return () => {
      Object.values(urls).forEach(u => URL.revokeObjectURL(u));
    };
  }, [files]);

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const totalCount = files.length + (forwardedAttachments?.length || 0);
  const isCompact = totalCount > 4;
  const thumbH = isCompact ? 'h-[60px]' : 'h-[80px]';
  const maxW = isCompact ? 'max-w-[110px]' : 'max-w-[140px]';
  const iconSz = isCompact ? 'w-6 h-6' : 'w-8 h-8';

  // Check if a file type can be previewed
  const canPreviewFile = (type: string) =>
    type.startsWith('image/') || type.includes('pdf') || type.startsWith('video/') || type.startsWith('audio/');

  return (
    <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
      {/* Accordion toggle header */}
      <button
        type="button"
        onClick={() => setCollapsed(prev => !prev)}
        className="flex items-center gap-1.5 w-full py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
      >
        <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="flex-1 text-left">
          {totalCount} Attachment{totalCount > 1 ? 's' : ''}
        </span>
        <ChevronDown className={cn("w-4 h-4 flex-shrink-0 transition-transform", collapsed && "-rotate-90")} />
      </button>

      {!collapsed && (
      <>
      {/* New file attachments - rendered with thumbnails and click-to-preview */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map((file, index) => {
            const isImage = file.type.startsWith('image/');
            const TypeIcon = getComposerFileIcon(file.type);
            const thumbUrl = objectUrls[index];
            const key = `new-${index}`;
            const previewable = canPreviewFile(file.type);

            return (
              <div
                key={key}
                className={cn(
                  'group relative bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden',
                  'w-full', maxW,
                  previewable && 'cursor-pointer hover:border-blue-500 dark:hover:border-blue-400'
                )}
                onClick={() => previewable && setPreviewFile(file)}
              >
                {/* Thumbnail or Icon */}
                <div className={cn(thumbH, 'w-full flex items-center justify-center bg-gray-100 dark:bg-gray-700/50 overflow-hidden relative')}>
                  {isImage && thumbUrl && !imgErrors[key] ? (
                    <>
                      {imgLoading[key] && (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute" />
                      )}
                      <img
                        src={thumbUrl}
                        alt={file.name}
                        className={cn('w-full h-full object-cover', imgLoading[key] ? 'opacity-0' : 'opacity-100')}
                        onLoad={() => setImgLoading(prev => ({ ...prev, [key]: false }))}
                        onError={() => {
                          setImgLoading(prev => ({ ...prev, [key]: false }));
                          setImgErrors(prev => ({ ...prev, [key]: true }));
                        }}
                      />
                    </>
                  ) : (
                    <TypeIcon className={cn(iconSz, 'text-gray-400')} />
                  )}
                </div>

                {/* File info */}
                <div className={cn(isCompact ? 'p-1' : 'p-1.5')}>
                  <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300 truncate" title={file.name}>
                    {file.name}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">
                    {formatSize(file.size)}
                  </p>
                </div>

                {/* Remove button */}
                <button
                  onClick={(e) => { e.stopPropagation(); onRemoveFile(index); }}
                  className="absolute top-1 right-1 p-0.5 bg-red-500/80 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  title="Remove attachment"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Forwarded attachments - use EmailAttachmentViewer for full thumbnail + preview support */}
      {forwardedAttachments && forwardedAttachments.length > 0 && (
        <div>
          {/* Label */}
          <div className="flex items-center gap-1.5 mb-1">
            <Paperclip className="w-3 h-3 text-blue-500" />
            <span className="text-[11px] font-medium text-blue-600 dark:text-blue-400">
              {forwardedAttachments.length} forwarded attachment{forwardedAttachments.length > 1 ? 's' : ''}
            </span>
          </div>
          {/* EmailAttachmentViewer handles thumbnails, PDF previews, full-screen modal + remove button */}
          <EmailAttachmentViewer
            attachments={forwardedAttachments.map(att => ({
              filename: att.filename,
              contentType: att.contentType,
              size: att.size,
              url: att.url,
              directUrl: att.directUrl,
              storage_token: att.storage_token,
              id: att.id,
              partId: att.partId,
            }))}
            messageId={messageId || ''}
            onRemoveAttachment={onRemoveForwarded}
            forceCompact
          />
        </div>
      )}

      {/* Full-screen preview modal for new File objects */}
      <ComposerFilePreviewModal
        file={previewFile}
        isOpen={previewFile !== null}
        onClose={() => setPreviewFile(null)}
      />
      </>
      )}
    </div>
  );
}

// Max concurrent compose windows
const MAX_COMPOSERS = 10;
const AUTO_SAVE_INTERVAL = 5000; // 5 seconds
const DRAFTS_STORAGE_KEY = 'email-drafts';
const RECENT_RECIPIENTS_STORAGE_KEY = 'recentEmailRecipients';
const COMPANY_EMAIL_USERS_STORAGE_KEY = 'companyEmailUsers';
const MAX_LOCAL_DRAFT_STORAGE_BYTES = 4_000_000;
let _cachedBannerSettings: BannerSettings | null = null;

function clearInvalidStoredValue(key: string) {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`[Composer] Failed to clear invalid localStorage item "${key}":`, error);
  }
}

function readStoredObject<T extends Record<string, any>>(key: string): T {
  if (typeof window === 'undefined') return {} as T;

  const rawValue = localStorage.getItem(key);
  if (!rawValue) return {} as T;

  try {
    const parsed = JSON.parse(rawValue);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as T;
    }

    console.warn(`[Composer] Expected object in localStorage for "${key}" but found`, typeof parsed);
  } catch (error) {
    console.warn(`[Composer] Invalid JSON found in localStorage for "${key}":`, error);
  }

  clearInvalidStoredValue(key);
  return {} as T;
}

function readStoredArray<T>(key: string): T[] {
  if (typeof window === 'undefined') return [];

  const rawValue = localStorage.getItem(key);
  if (!rawValue) return [];

  try {
    const parsed = JSON.parse(rawValue);
    if (Array.isArray(parsed)) {
      return parsed as T[];
    }

    console.warn(`[Composer] Expected array in localStorage for "${key}" but found`, typeof parsed);
  } catch (error) {
    console.warn(`[Composer] Invalid JSON found in localStorage for "${key}":`, error);
  }

  clearInvalidStoredValue(key);
  return [];
}

function getStoredJsonSize(value: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }

  return value.length;
}

// Helper to safely get email body as string (handles {text, html} object format in drafts)
// Also checks html_content/body_html/text_content/body_text variants from thread messages
// The result is run through sanitizeEmailHtml (same pipeline as the thread viewer) so
// quoted/forwarded mail can't trigger remote fetches the app CSP would block anyway
// (fonts, stylesheets, scripts) and cid: images resolve to real attachment URLs.
function getEmailBodyAsHtml(email: any): string {
  let raw: unknown = '';
  if (email.html) raw = email.html;
  // Check thread message field names (body_html / html_content)
  else if (typeof email.body_html === 'string' && email.body_html) raw = email.body_html;
  else if (typeof email.html_content === 'string' && email.html_content) raw = email.html_content;
  else if (email.body && typeof email.body === 'object') {
    raw = (email.body as any).html || (email.body as any).text || '';
  } else if (typeof email.body === 'string') raw = email.body;
  else if (email.text) raw = email.text;
  // Check thread message field names (body_text / text_content)
  else if (typeof email.body_text === 'string' && email.body_text) raw = email.body_text;
  else if (typeof email.text_content === 'string' && email.text_content) raw = email.text_content;

  if (!raw) return '';

  const fromAddress =
    typeof email.from === 'string'
      ? email.from
      : email.from?.email || email.from?.address || '';
  const senderDomain = fromAddress.includes('@') ? fromAddress.split('@').pop() : undefined;

  return sanitizeEmailHtml(raw, { attachments: email.attachments, senderDomain });
}

/**
 * Generates inline HTML for the security banner to include in trailing email content.
 * Renders a static replica of the collapsed EmailSecurityBanner component.
 * Returns empty string if no notable security findings.
 */
function buildSecurityBannerHtml(email: any): string {
  const sa = email.security_analysis as (SecurityAnalysis & Record<string, any>) | undefined;
  if (!sa) return '';

  // Also check deep analysis fields stored inline on security_analysis
  const deepThreatScore = sa.deep_threat_score as number | undefined;
  const deepThreatCategory = sa.deep_threat_category as string | undefined;
  const hasDeepThreat = deepThreatScore != null && deepThreatScore >= 30;

  if (sa.warning_level === 'none' && !sa.is_spam && !sa.is_potential_impersonation && !hasDeepThreat) return '';

  // Determine banner styling based on severity (mirrors BANNER_CONFIGS)
  let bgColor = '#fffbeb';    // amber-50
  let borderColor = '#fcd34d'; // amber-300
  let textColor = '#92400e';   // amber-800
  let title = 'Security Notice';

  if (sa.is_potential_impersonation) {
    bgColor = '#fef2f2'; borderColor = '#fecaca'; textColor = '#991b1b';
    title = 'Total Threat Analysis - Potential Impersonation Attempt';
  } else if (hasDeepThreat && deepThreatCategory) {
    const catLabel = deepThreatCategory.charAt(0).toUpperCase() + deepThreatCategory.slice(1);
    if (deepThreatScore >= 40 || ['phishing', 'bec', 'spoofing'].includes(deepThreatCategory)) {
      bgColor = '#fef2f2'; borderColor = '#fecaca'; textColor = '#991b1b';
      title = `Total Threat Analysis - ${catLabel === 'Bec' ? 'BEC Risk' : catLabel === 'Phishing' ? 'Phishing Threat' : catLabel}`;
    } else {
      // Use client's custom banner label and severity if configured
      const customLabel = _cachedBannerSettings?.external_banner_label || 'Suspicious Sender';
      const customSeverity = _cachedBannerSettings?.external_banner_severity || 'warning';
      if (customSeverity === 'danger') {
        bgColor = '#fef2f2'; borderColor = '#fecaca'; textColor = '#991b1b';
      } else if (customSeverity === 'info') {
        bgColor = '#f8fafc'; borderColor = '#cbd5e1'; textColor = '#475569';
      } else {
        bgColor = '#fff7ed'; borderColor = '#fed7aa'; textColor = '#9a3412';
      }
      title = `Total Threat Analysis - ${customLabel}`;
    }
  } else if (sa.warning_level === 'critical' || sa.warning_level === 'high' || sa.is_spam) {
    if (sa.is_spam) {
      bgColor = '#fef2f2'; borderColor = '#fecaca'; textColor = '#991b1b';
      title = 'Spam Detected';
    } else {
      // Use client's custom banner label and severity if configured
      const customLabel = _cachedBannerSettings?.external_banner_label || 'Security Warning';
      const customSeverity = _cachedBannerSettings?.external_banner_severity || 'danger';
      if (customSeverity === 'danger') {
        bgColor = '#fef2f2'; borderColor = '#fecaca'; textColor = '#991b1b';
      } else if (customSeverity === 'info') {
        bgColor = '#f8fafc'; borderColor = '#cbd5e1'; textColor = '#475569';
      } else {
        bgColor = '#fff7ed'; borderColor = '#fed7aa'; textColor = '#9a3412';
      }
      title = `Total Threat Analysis - ${customLabel}`;
    }
  } else if (sa.warning_level === 'medium') {
    // Use client's custom banner label and severity if configured
    const customLabel = _cachedBannerSettings?.external_banner_label || 'Security Notice';
    const customSeverity = _cachedBannerSettings?.external_banner_severity || 'warning';
    if (customSeverity === 'danger') {
      bgColor = '#fef2f2'; borderColor = '#fecaca'; textColor = '#991b1b';
    } else if (customSeverity === 'info') {
      bgColor = '#f8fafc'; borderColor = '#cbd5e1'; textColor = '#475569';
    } else {
      bgColor = '#fff7ed'; borderColor = '#fed7aa'; textColor = '#9a3412';
    }
    title = `Total Threat Analysis - ${customLabel}`;
  }

  // Build subtitle line (mirrors the threat summary under the title)
  const subtitleParts: string[] = [];
  if (deepThreatScore != null && deepThreatScore >= 20) {
    subtitleParts.push(`Threat Score: ${Math.round(deepThreatScore)}/100`);
  }
  if (deepThreatCategory && deepThreatCategory !== 'clean') {
    subtitleParts.push(deepThreatCategory.charAt(0).toUpperCase() + deepThreatCategory.slice(1));
  }
  if (sa.is_spam && !subtitleParts.length) {
    subtitleParts.push(`Spam score: ${sa.spam_score}`);
  }
  // Domain reputation if negative
  const domainRep = (sa as any).deep_domain_reputation_score ?? (sa as any).domain_reputation_score;
  if (domainRep != null && domainRep < 0) {
    subtitleParts.push(`Domain reputation: ${domainRep}`);
  }

  // Risk badge
  let badgeBg = '#dcfce7'; let badgeColor = '#166534'; let badgeLabel = 'Low Risk';
  if (deepThreatScore != null) {
    if (deepThreatScore >= 70) { badgeBg = '#fecaca'; badgeColor = '#991b1b'; badgeLabel = 'High Risk'; }
    else if (deepThreatScore >= 40) { badgeBg = '#fed7aa'; badgeColor = '#9a3412'; badgeLabel = 'Medium Risk'; }
    else if (deepThreatScore >= 20) { badgeBg = '#fef08a'; badgeColor = '#854d0e'; badgeLabel = 'Low Risk'; }
    else { badgeBg = '#dcfce7'; badgeColor = '#166534'; badgeLabel = 'Clean'; }
  } else if (sa.warning_level === 'critical' || sa.warning_level === 'high' || sa.is_potential_impersonation || sa.is_spam) {
    // For impersonation/spam keep red badge; for other external sender warnings respect custom severity
    if (sa.is_potential_impersonation || sa.is_spam) {
      badgeBg = '#fecaca'; badgeColor = '#991b1b'; badgeLabel = 'High Risk';
    } else {
      const customSeverity = _cachedBannerSettings?.external_banner_severity || 'danger';
      if (customSeverity === 'danger') {
        badgeBg = '#fecaca'; badgeColor = '#991b1b'; badgeLabel = 'High Risk';
      } else if (customSeverity === 'info') {
        badgeBg = '#e2e8f0'; badgeColor = '#475569'; badgeLabel = 'Notice';
      } else {
        badgeBg = '#fed7aa'; badgeColor = '#9a3412'; badgeLabel = 'Caution';
      }
    }
  } else if (sa.warning_level === 'medium') {
    const customSeverity = _cachedBannerSettings?.external_banner_severity || 'warning';
    if (customSeverity === 'danger') {
      badgeBg = '#fecaca'; badgeColor = '#991b1b'; badgeLabel = 'High Risk';
    } else if (customSeverity === 'info') {
      badgeBg = '#e2e8f0'; badgeColor = '#475569'; badgeLabel = 'Notice';
    } else {
      badgeBg = '#fed7aa'; badgeColor = '#9a3412'; badgeLabel = 'Caution';
    }
  }

  // Use absolute URL for the icon so it renders in email clients
  const iconUrl = `https://dev-next.totalaccess.co.za/icons/threat-analysis-icon-sml.png`;
  const subtitle = subtitleParts.length ? subtitleParts.join(' &bull; ') : '';

  return `
<div style="margin: 12px 0; display: flex; align-items: center; background: ${bgColor}; border: 1px solid ${borderColor}; border-radius: 8px; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
  <img src="${iconUrl}" alt="Security" width="52" height="52" style="flex-shrink: 0; margin: 0; padding: 0; display: block;" />
  <div style="flex: 1; min-width: 0; padding: 8px 0 8px 12px;">
    <div style="font-weight: 600; font-size: 13px; color: ${textColor}; margin: 0; line-height: 1.3;">${title}</div>
    ${subtitle ? `<div style="font-size: 11px; color: ${textColor}; opacity: 0.75; margin-top: 2px; line-height: 1.3;">${subtitle}</div>` : ''}
  </div>
  <div style="flex-shrink: 0; padding: 0 12px;">
    <span style="display: inline-block; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; background: ${badgeBg}; color: ${badgeColor};">${badgeLabel}</span>
  </div>
</div>`;
}

/**
 * Parse email recipients from various formats (string or array)
 */
function parseEmailRecipients(recipients: unknown): Array<{ name?: string; address?: string; email?: string }> {
  if (!recipients) return [];

  if (Array.isArray(recipients)) {
    return recipients.map(r => {
      if (typeof r === 'string') {
        const angle = r.match(/<([^<>]+@[^<>]+)>/);
        if (angle) {
          const name = r.slice(0, angle.index).trim().replace(/^['"]+|['"]+$/g, '') || undefined;
          const addr = angle[1].trim();
          return { name, address: addr, email: addr };
        }
        return { address: r, email: r };
      }
      return { name: r.name, address: r.address || r.email, email: r.email || r.address };
    });
  }

  if (typeof recipients === 'string') {
    return recipients.split(',').map(s => {
      const trimmed = s.trim();
      const angle = trimmed.match(/<([^<>]+@[^<>]+)>/);
      if (angle) {
        const name = trimmed.slice(0, angle.index).trim().replace(/^['"]+|['"]+$/g, '') || undefined;
        const addr = angle[1].trim();
        return { name, address: addr, email: addr };
      }
      return { address: trimmed, email: trimmed };
    }).filter(r => r.address || r.email);
  }

  return [];
}

/**
 * Resolve internal totalhub.online emails to business/primary emails
 * Uses cached company contacts from localStorage to map totalhub.online -> business email
 */
function resolveToBusinessEmail(email: string): string {
  if (!email) return email;

  // If it's not a totalhub.online email, return as-is
  if (!email.includes('totalhub.online')) {
    return email;
  }

  try {
    // Get company contacts from localStorage
    const contacts = readStoredArray<any>(COMPANY_EMAIL_USERS_STORAGE_KEY);
    if (!contacts.length) return email;

    // Find contact by matching any email in all_emails
    for (const contact of contacts) {
      const allEmails = contact.all_emails || [];
      if (allEmails.some((e: string) => e?.toLowerCase() === email.toLowerCase())) {
        // Return the primary/business email (not the totalhub.online one)
        const businessEmail = contact.email || contact.primary_email;
        if (businessEmail && !businessEmail.includes('totalhub.online')) {
          return businessEmail;
        }
      }
    }

    // No match found, return original
    return email;
  } catch (err) {
    console.error('[Composer] Error resolving business email:', err);
    return email;
  }
}

/**
 * Resolve email to full contact info including display name
 * Returns { email, name, formatted } where formatted is "Name <email>"
 */
function resolveToContact(email: string, fallbackName?: string): { email: string; name: string; formatted: string } {
  if (!email) return { email: '', name: '', formatted: '' };

  const resolvedEmail = resolveToBusinessEmail(email);

  try {
    // Get company contacts from localStorage
    const contacts = readStoredArray<any>(COMPANY_EMAIL_USERS_STORAGE_KEY);
    if (contacts.length > 0) {
      // Find contact by matching any email in all_emails OR the resolved email
      for (const contact of contacts) {
        const allEmails = contact.all_emails || [];
        const emailMatch = allEmails.some((e: string) => e?.toLowerCase() === email.toLowerCase()) ||
          contact.email?.toLowerCase() === resolvedEmail.toLowerCase();

        if (emailMatch) {
          const displayName = contact.name ||
            (contact.first_name && contact.last_name ? `${contact.first_name} ${contact.last_name}` : '') ||
            fallbackName ||
            resolvedEmail.split('@')[0];

          return {
            email: resolvedEmail,
            name: displayName,
            formatted: displayName ? `${displayName} <${resolvedEmail}>` : resolvedEmail
          };
        }
      }
    }
  } catch (err) {
    console.error('[Composer] Error resolving contact:', err);
  }

  // No match found - use fallback name or extract from email
  const name = fallbackName || resolvedEmail.split('@')[0];
  return {
    email: resolvedEmail,
    name,
    formatted: name ? `${name} <${resolvedEmail}>` : resolvedEmail
  };
}

// Shared mailbox account info for send-as/send-on-behalf
export interface FromAccount {
  mailboxId: number;
  emailAddress: string;
  displayName: string;
  isShared: boolean;
  isPartnerAccount?: boolean;
  partnerClientId?: number;
  partnerClientName?: string;
  permissions?: {
    canSend?: boolean;
    canSendAs?: boolean;
    canSendOnBehalf?: boolean;
  };
  // Connector settings
  isConnector?: boolean;
  connectorId?: number;
  // Alias settings
  isAlias?: boolean;
  aliasId?: number;
  // Primary/System settings
  isPrimary?: boolean;
  isSystemAddress?: boolean; // The underlying totalhub.online address
  // DNS/Deliverability status
  hasDeliveryIssues?: boolean;
  deliverabilityScore?: number | null;
  // Team mailbox settings
  isTeamMailbox?: boolean;
  teamId?: string;
  teamName?: string;
  teamColor?: string;
  // Cross-client personal mailbox (user's own mailbox at another client)
  isCrossClientPersonal?: boolean;
}

function normalizePositiveNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function resolvePreferredFromAccount(
  availableFromAccounts: FromAccount[],
  preferredMailboxId?: number | null,
): FromAccount | null {
  if (availableFromAccounts.length === 0) {
    return null;
  }

  if (preferredMailboxId) {
    const mailboxAccounts = availableFromAccounts.filter((account) => account.mailboxId === preferredMailboxId);
    if (mailboxAccounts.length > 0) {
      return (
        mailboxAccounts.find((account) => account.isPrimary) ||
        mailboxAccounts.find((account) => account.isConnector) ||
        mailboxAccounts[0]
      );
    }
  }

  const hasPersonalAccount = availableFromAccounts.some((account) => !account.isShared);
  if (hasPersonalAccount) {
    return availableFromAccounts[0];
  }

  return availableFromAccounts.find((account) => account.isConnector) || availableFromAccounts[0];
}

function getFromAccountStatusLabel(
  account: FromAccount | null | undefined,
  sendAs?: boolean,
  sendOnBehalf?: boolean,
): string | null {
  if (!account) return null;
  if (account.isPartnerAccount) {
    return account.partnerClientName ? `Partner • ${account.partnerClientName}` : 'Partner Mailbox';
  }
  if (account.isTeamMailbox) {
    return account.teamName ? `Team • ${account.teamName}` : 'Team Mailbox';
  }
  if (account.isShared) {
    if (sendAs) return 'Shared • Send As';
    if (sendOnBehalf) return 'Shared • Send on Behalf';
    return 'Shared Mailbox';
  }
  return null;
}

export interface ComposerInstance {
  id: string;
  replyTo?: {
    email: Email;
    all?: boolean;
    forward?: boolean;
  } | null;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  bodyText: string;
  attachments: File[];
  forwardedAttachments?: Attachment[]; // Original attachments from forwarded email
  isMinimized: boolean;
  isMaximized: boolean;
  createdAt: number;
  lastSavedAt: number;
  isDirty: boolean;
  // Server draft message ID (for updating existing drafts)
  draftMessageId?: string | null;
  // Shared mailbox send options
  fromAccount?: FromAccount | null;
  sendAs?: boolean; // If fromAccount is shared mailbox, send as that mailbox
  sendOnBehalf?: boolean; // If fromAccount is shared mailbox, send on behalf of that mailbox
  // Scheduled send
  scheduledAt?: Date | null;
  // Meeting invite
  meetingInvite?: {
    title: string;
    startTime: Date;
    endTime: Date;
    location?: string;
    description?: string;
  } | null;
}

interface StoredComposerDraft {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body?: string;
  bodyText?: string;
  replyTo?: ComposerInstance['replyTo'] | null;
  savedAt?: number;
}

type StoredComposerDraftMap = Record<string, StoredComposerDraft>;

function readStoredDrafts(): StoredComposerDraftMap {
  return readStoredObject<StoredComposerDraftMap>(DRAFTS_STORAGE_KEY);
}

function findOldestRemovableDraftIndex(
  entries: Array<[string, StoredComposerDraft]>,
  protectedDraftId?: string
): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    if (entries[index][0] !== protectedDraftId) {
      return index;
    }
  }

  return -1;
}

function writeStoredDrafts(drafts: StoredComposerDraftMap, protectedDraftId?: string): boolean {
  if (typeof window === 'undefined') return false;

  let prioritizedDraftId = protectedDraftId;
  let entries = Object.entries(drafts)
    .filter(([, draft]) => !!draft)
    .sort(([, left], [, right]) => (right.savedAt || 0) - (left.savedAt || 0))
    .slice(0, MAX_COMPOSERS);

  while (entries.length > 0) {
    const nextDrafts = Object.fromEntries(entries) as StoredComposerDraftMap;
    const serializedDrafts = JSON.stringify(nextDrafts);

    if (getStoredJsonSize(serializedDrafts) > MAX_LOCAL_DRAFT_STORAGE_BYTES) {
      const removableIndex = findOldestRemovableDraftIndex(entries, prioritizedDraftId);

      if (removableIndex >= 0) {
        entries.splice(removableIndex, 1);
        continue;
      }

      if (prioritizedDraftId) {
        console.warn(`[Composer] Draft ${prioritizedDraftId} is too large for localStorage; keeping server copy only.`);
        entries = entries.filter(([draftId]) => draftId !== prioritizedDraftId);
        prioritizedDraftId = undefined;
        continue;
      }

      entries.pop();
      continue;
    }

    try {
      localStorage.setItem(DRAFTS_STORAGE_KEY, serializedDrafts);
      return !protectedDraftId || Boolean(nextDrafts[protectedDraftId]);
    } catch (error) {
      console.warn('[Composer] Failed to persist drafts to localStorage:', error);

      const removableIndex = findOldestRemovableDraftIndex(entries, prioritizedDraftId);
      if (removableIndex >= 0) {
        entries.splice(removableIndex, 1);
        continue;
      }

      if (prioritizedDraftId) {
        entries = entries.filter(([draftId]) => draftId !== prioritizedDraftId);
        prioritizedDraftId = undefined;
        continue;
      }

      break;
    }
  }

  try {
    localStorage.removeItem(DRAFTS_STORAGE_KEY);
  } catch (error) {
    console.warn('[Composer] Failed to remove local draft cache:', error);
  }

  return false;
}

/** Detect meaningful text in HTML without treating tags as content. */
function stripHtmlForDraftPresence(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** True if the composer should be persisted when closed (dirty or any recipient/subject/body). */
function composerHasPersistableContent(
  instance: ComposerInstance,
  htmlBody: string,
  textBody: string,
): boolean {
  if (instance.isDirty) return true;
  if (instance.to.trim() || instance.cc.trim() || instance.bcc.trim() || instance.subject.trim()) return true;
  if (textBody.trim()) return true;
  if (stripHtmlForDraftPresence(htmlBody)) return true;
  return false;
}

/**
 * Writes compose snapshot to localStorage and TotalAccessOfflineDB (`emails` / `email_drafts` via `offlineEmail` → `emailsStore` → `offlineDb`).
 */
async function persistComposerDraftToStorageAndOfflineDb(args: {
  instance: ComposerInstance;
  htmlBody: string;
  textBody: string;
  availableFromAccounts: FromAccount[];
  onUpdate: (updates: Partial<ComposerInstance>) => void;
}): Promise<void> {
  const { instance, htmlBody, textBody, availableFromAccounts, onUpdate } = args;

  const drafts = readStoredDrafts();
  drafts[instance.id] = {
    to: instance.to,
    cc: instance.cc,
    bcc: instance.bcc,
    subject: instance.subject,
    body: htmlBody,
    bodyText: textBody,
    replyTo: instance.replyTo,
    savedAt: Date.now(),
  };
  writeStoredDrafts(drafts, instance.id);
  onUpdate({ lastSavedAt: Date.now(), isDirty: false });

  const hasPayload =
    instance.to.trim() ||
    instance.subject.trim() ||
    htmlBody.trim() ||
    textBody.trim();
  if (!hasPayload) {
    return;
  }

  try {
    const { offlineEmail } = await import('@/lib/offlineEmail');
    const toEmails = instance.to.split(',').map((e) => e.trim()).filter(Boolean);
    const ccEmails = instance.cc.split(',').map((e) => e.trim()).filter(Boolean);
    const bccEmails = instance.bcc.split(',').map((e) => e.trim()).filter(Boolean);
    const mailboxEmail = instance.fromAccount?.emailAddress || availableFromAccounts[0]?.emailAddress || '';

    await offlineEmail.saveDraft({
      draftId: instance.draftMessageId || instance.id,
      mailboxEmail,
      to: toEmails,
      cc: ccEmails.length > 0 ? ccEmails : undefined,
      bcc: bccEmails.length > 0 ? bccEmails : undefined,
      subject: instance.subject,
      htmlBody,
      textBody,
      inReplyToId: instance.replyTo?.email?.messageId,
      threadId: instance.replyTo?.email?.threadId,
      attachments: [],
      autoSaved: true,
    });
  } catch (error) {
    console.warn('[Composer] Failed to persist draft to IndexedDB (TotalAccessOfflineDB):', error);
  }

  try {
    const accountId = instance.fromAccount?.mailboxId || availableFromAccounts[0]?.mailboxId;
    const result = await emailApiService.saveDraft({
      to: instance.to,
      cc: instance.cc,
      bcc: instance.bcc,
      subject: instance.subject,
      body: htmlBody,
      accountId,
      draftId: instance.draftMessageId || undefined,
    });

    if (result.success && result.data?.messageId) {
      onUpdate({ draftMessageId: result.data.messageId });
      const { folderThreadsLastSync, activeSharedMailboxId, selectedFolder, fetchThreads: storeFetchThreads } =
        useEmailStore.getState();
      const draftsCacheKey = `Drafts:${activeSharedMailboxId || 0}`;
      useEmailStore.setState({
        folderThreadsLastSync: { ...folderThreadsLastSync, [draftsCacheKey]: 0 },
      });
      if (selectedFolder === 'Drafts') {
        storeFetchThreads({ folder: 'Drafts', forceRefresh: true });
      }
    } else if (result.error) {
      console.warn('[Composer] Server draft save failed:', result.error);
    }
  } catch (serverError) {
    console.warn('[Composer] Failed to save draft to server:', serverError);
  }
}

// Sent email data for optimistic updates
export interface SentEmailData {
  messageId: string;
  to: { email: string; name?: string }[];
  cc?: { email: string; name?: string }[];
  bcc?: { email: string; name?: string }[];
  subject: string;
  body: string;
  html?: string;
  date: Date;
  fromAccount?: FromAccount;
}

interface MultiComposeManagerProps {
  composers: ComposerInstance[];
  activeComposerId: string | null;
  availableFromAccounts?: FromAccount[]; // List of accounts user can send from
  onUpdateComposer: (id: string, updates: Partial<ComposerInstance>) => void;
  onCloseComposer: (id: string) => void;
  onMinimizeComposer: (id: string) => void;
  onRestoreComposer: (id: string) => void;
  onMaximizeComposer: (id: string) => void;
  onSent: (id: string, sentEmail?: SentEmailData) => void;
  onBackToThreadList?: () => void;
  /**
   * `centered` — viewport modal (e.g. Enhanced Email module / full page).
   * `desktop-email-slide` — right sheet inside the desktop email slide shell (EmailMobileModal `desktop-slide`).
   */
  composePresentation?: 'centered' | 'desktop-email-slide';
  /** When false, hides expand/fullscreen control (slide sheet from email modal). */
  showComposerMaximize?: boolean;
}

interface EmailSuggestion {
  email: string;
  name?: string;
  source: 'recent' | 'contact' | 'company' | 'crm' | 'personal';
  avatar_url?: string;
  job_title?: string;
  contact_type?: 'customer' | 'supplier' | 'both' | 'lead' | 'prospect' | string;
  company?: string;
}

interface CompanyContact {
  id: number;
  email: string;
  personal_email?: string;
  primary_email?: string;
  all_emails?: string[];
  first_name?: string;
  last_name?: string;
  name?: string;
  job_title?: string;
  avatar_url?: string;
  is_user?: boolean;
}

interface ContactPickerContact {
  id: string | number;
  email: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  company?: string;
  job_title?: string;
  contact_type?: string;
  avatar_url?: string;
  photo_url?: string;
  department?: string;
  phone?: string;
  mobile?: string;
  is_user?: boolean;
  is_company?: boolean;
  is_person?: boolean;
}

interface CannedResponseRecord {
  id: string;
  title: string;
  content: string;
  category?: string;
  shortcut_key?: string;
  is_active?: boolean;
}

// Canned Response Editor Modal — create/edit/delete
function CannedResponseEditorModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  editingResponse,
  headerBgColor,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { id?: string; title: string; content: string; category?: string; shortcut_key?: string }) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  editingResponse: CannedResponseRecord | null;
  headerBgColor: string;
}) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [shortcutKey, setShortcutKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isMobile = isCapacitor() || (typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    if (isOpen && editingResponse) {
      setTitle(editingResponse.title);
      setContent(editingResponse.content);
      setCategory(editingResponse.category || '');
      setShortcutKey(editingResponse.shortcut_key || '');
    } else if (isOpen && !editingResponse) {
      setTitle('');
      setContent('');
      setCategory('');
      setShortcutKey('');
    }
  }, [isOpen, editingResponse]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    try {
      await onSave({
        id: editingResponse?.id,
        title: title.trim(),
        content: content.trim(),
        category: category.trim() || undefined,
        shortcut_key: shortcutKey.trim() || undefined,
      });
      onClose();
    } catch (err) {
      console.error('Failed to save canned response:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingResponse?.id || !onDelete) return;
    setDeleting(true);
    try {
      await onDelete(editingResponse.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete canned response:', err);
    } finally {
      setDeleting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[210] p-4"
      style={isMobile ? {
        top: 'var(--safe-area-inset-top, 0px)',
        bottom: 'var(--safe-area-inset-bottom, 0px)',
      } : undefined}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={cn(
        "bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full flex flex-col",
        isMobile ? 'max-h-full max-w-full h-full rounded-none' : 'max-w-lg max-h-[80vh]'
      )}>
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 flex-shrink-0 rounded-t-xl"
          style={{ backgroundColor: headerBgColor }}
        >
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Zap className="w-4 h-4" />
            {editingResponse ? 'Edit Quick Response' : 'New Quick Response'}
          </h3>
          <button onClick={onClose} className="p-1.5 text-white/80 hover:text-white rounded-lg hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Greeting, Resolution, Escalation"
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Content <span className="text-red-500">*</span></label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Type the response content here... You can use {{agent.name}} and {{date.today}} as variables."
              rows={isMobile ? 6 : 5}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">Supports HTML. Variables: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'{{agent.name}}'}</code>, <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'{{date.today}}'}</code></p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. greeting, billing"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Shortcut Key</label>
              <input
                type="text"
                value={shortcutKey}
                onChange={(e) => setShortcutKey(e.target.value.replace(/\s/g, ''))}
                placeholder="e.g. hi, thanks"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-xl">
          <div>
            {editingResponse && onDelete && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!title.trim() || !content.trim() || saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 transition-colors"
              style={{ backgroundColor: headerBgColor }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editingResponse ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function isStatusLikeError(error: unknown): error is { message?: string; status?: number; response?: { status?: number } } {
  return typeof error === 'object' && error !== null;
}

// Single Composer Component
function ComposerWindow({
  instance,
  isActive,
  availableFromAccounts = [],
  composePresentation = 'centered',
  showComposerMaximize = true,
  onUpdate,
  onClose,
  onMinimize,
  onMaximize,
  onSent,
  onBackToThreadList,
}: {
  instance: ComposerInstance;
  isActive: boolean;
  availableFromAccounts?: FromAccount[];
  composePresentation?: 'centered' | 'desktop-email-slide';
  showComposerMaximize?: boolean;
  onUpdate: (updates: Partial<ComposerInstance>) => void;
  onClose: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
  onSent: (sentEmail?: SentEmailData) => void;
  onBackToThreadList?: () => void;
}) {
  const { clientBranding, primaryColor, isDark } = useTheme();
  const { client } = useAuth();
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(!!instance.cc);
  const [showBcc, setShowBcc] = useState(!!instance.bcc);
  const [recipientsCollapsed, setRecipientsCollapsed] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const userCollapsedRecipients = useRef(false);

  // Auto-reveal CC/BCC fields when recipients are present (e.g. populated by Reply All)
  useEffect(() => {
    const hasCcRecipients = Array.isArray(instance.cc)
      ? instance.cc.length > 0
      : !!instance.cc;
    if (hasCcRecipients) setShowCc(true);
  }, [instance.cc]);

  useEffect(() => {
    const hasBccRecipients = Array.isArray(instance.bcc)
      ? instance.bcc.length > 0
      : !!instance.bcc;
    if (hasBccRecipients) setShowBcc(true);
  }, [instance.bcc]);
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState<EmailSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState<'to' | 'cc' | 'bcc' | null>(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [signature, setSignature] = useState<string>('');
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showMeetingCreator, setShowMeetingCreator] = useState(false);
  const [showCannedResponses, setShowCannedResponses] = useState(false);
  const [cannedResponses, setCannedResponses] = useState<Array<{ id: string; title: string; content: string; category?: string; shortcut_key?: string }>>([]);
  const [cannedResponsesSearch, setCannedResponsesSearch] = useState('');
  const [loadingCannedResponses, setLoadingCannedResponses] = useState(false);
  const [showCannedResponseEditor, setShowCannedResponseEditor] = useState(false);
  const [editingCannedResponse, setEditingCannedResponse] = useState<CannedResponseRecord | null>(null);
  const [generatingAiSuggestion, setGeneratingAiSuggestion] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [companyContacts, setCompanyContacts] = useState<CompanyContact[]>([]);
  const [crmContacts, setCrmContacts] = useState<ContactPickerContact[]>([]);
  const [personalContacts, setPersonalContacts] = useState<ContactPickerContact[]>([]);
  const [contactPickerTab, setContactPickerTab] = useState<'company' | 'crm' | 'personal'>('company');
  const [crmContactTypeFilter, setCrmContactTypeFilter] = useState<string>('all'); // Filter for CRM contacts
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const ccInputRef = useRef<HTMLInputElement>(null);
  const bccInputRef = useRef<HTMLInputElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const bodyEditorRef = useRef<RichTextEditorRef>(null);
  const instanceRef = useRef(instance);
  instanceRef.current = instance;
  const fromDropdownRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Get the header color from client branding
  const headerBgColor = clientBranding?.primary_color || primaryColor || '#3B82F6';

  // Detect mobile (both Capacitor and small screens)
  const isMobile = isCapacitor() || (typeof window !== 'undefined' && window.innerWidth < 768);

  // Detect native keyboard visibility via VisualViewport API
  // When keyboard appears, auto-collapse recipients/subject to maximize typing area
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;
    const baselineHeight = vv.height;
    const handleResize = () => {
      // Keyboard is considered visible if viewport shrank by more than 150px
      const shrank = baselineHeight - vv.height > 150;
      setKeyboardVisible(shrank);
      if (shrank) setRecipientsCollapsed(true);
    };
    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Auto-expand recipients when keyboard dismisses (unless user manually collapsed)
  useEffect(() => {
    if (isMobile && !keyboardVisible && !userCollapsedRecipients) {
      setRecipientsCollapsed(false);
    }
  }, [keyboardVisible, isMobile, userCollapsedRecipients]);

  // Mobile back button handler — closes composer without closing parent modal
  const { registerBackHandler, unregisterBackHandler } = useModalState();
  useEffect(() => {
    if (!isActive) return;
    const handleBack = (): boolean => {
      void (async () => {
        const inst = instanceRef.current;
        const htmlBody = bodyEditorRef.current?.getHTML() || inst.body;
        const textBody = bodyEditorRef.current?.getText() || inst.bodyText;
        if (composerHasPersistableContent(inst, htmlBody, textBody)) {
          await persistComposerDraftToStorageAndOfflineDb({
            instance: inst,
            htmlBody,
            textBody,
            availableFromAccounts,
            onUpdate,
          });
        }
        onClose();
        onBackToThreadList?.();
      })();
      return true;
    };
    registerBackHandler(handleBack);
    return () => unregisterBackHandler(handleBack);
  }, [
    isActive,
    onBackToThreadList,
    onClose,
    onUpdate,
    availableFromAccounts,
    registerBackHandler,
    unregisterBackHandler,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target as Node)) {
        setShowFromDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus the To field when the composer opens (new compose only, not reply/forward)
  // Use a delay so focus runs after RichTextEditor + signature initialization
  useEffect(() => {
    if (!isActive) return;
    // Only auto-focus To field for new emails (reply/forward already have recipients)
    if (instance.replyTo) return;

    const timer = setTimeout(() => {
      toInputRef.current?.focus();
      // Scroll the composer to the top so To field is visible
      toInputRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const loadCompanyContacts = async () => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ action: 'getCompanyUsers' })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setCompanyContacts(data.data);
          // Also update localStorage for quick suggestions
          // Include all possible email addresses for avatar lookup (from all_emails array)
          const formatted = data.data.map((c: CompanyContact) => ({
            email: c.email,
            personal_email: c.personal_email,
            primary_email: c.primary_email,
            // Use all_emails from API which includes totalhub.online addresses
            all_emails: c.all_emails || [c.email, c.personal_email, c.primary_email].filter(Boolean),
            name: c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : c.name || c.email,
            source: 'company',
            avatar_url: c.avatar_url,
            job_title: c.job_title
          }));
          localStorage.setItem('companyEmailUsers', JSON.stringify(formatted));
        }
      }
    } catch (err) {
      console.error('Failed to load company contacts:', err);
    }
  };

  // Load company contacts on mount
  useEffect(() => {
    loadCompanyContacts();
  }, []);

  // Auto-select first available from account if none is set
  // This is important for virtual mailbox users who only have shared mailbox access
  useEffect(() => {
    if (!instance.fromAccount && availableFromAccounts.length > 0) {
      const replyMailboxId = normalizePositiveNumber(
        (instance.replyTo?.email as Record<string, unknown> | undefined)?.account_id ??
        (instance.replyTo?.email as Record<string, unknown> | undefined)?.accountId,
      );
      const defaultAccount = resolvePreferredFromAccount(availableFromAccounts, replyMailboxId);
      if (!defaultAccount) {
        return;
      }
      console.log('[Composer] Auto-selecting default from account:', defaultAccount.emailAddress);
      onUpdate({
        fromAccount: defaultAccount,
        sendAs: defaultAccount.isShared ? true : undefined,
        sendOnBehalf: undefined,
      });
    }
  }, [instance.fromAccount, instance.replyTo?.email, availableFromAccounts, onUpdate]);

  // Load CRM contacts (customers, suppliers, vendors)
  const loadCrmContacts = async () => {
    setLoadingContacts(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ action: 'getCrmContacts', limit: 100 })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Combine contacts and contact people into a single list
          const combined = [
            ...(data.data.contactPeople || []),
            ...(data.data.contacts || [])
          ];
          // Deduplicate by email to prevent adding same contact twice
          const seen = new Set<string>();
          const deduped = combined.filter(c => {
            if (!c.email) return false;
            const emailLower = c.email.toLowerCase();
            if (seen.has(emailLower)) return false;
            seen.add(emailLower);
            return true;
          });
          setCrmContacts(deduped);
        }
      }
    } catch (err) {
      console.error('Failed to load CRM contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Load personal contacts (MyContacts)
  const loadPersonalContacts = async () => {
    setLoadingContacts(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ action: 'getMyContacts', limit: 100 })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setPersonalContacts(data.data);
        }
      }
    } catch (err) {
      console.error('Failed to load personal contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Load canned responses for quick insert
  const loadCannedResponses = async () => {
    setLoadingCannedResponses(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const params = new URLSearchParams();
      if (cannedResponsesSearch) params.append('search', cannedResponsesSearch);

      const response = await fetch(`/api/v1/work-items/canned-responses?${params}`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setCannedResponses((data.data as CannedResponseRecord[]).filter((response) => response.is_active !== false));
        }
      }
    } catch (err) {
      console.error('Failed to load canned responses:', err);
    } finally {
      setLoadingCannedResponses(false);
    }
  };

  // Save (create or update) a canned response
  const handleSaveCannedResponse = async (data: { id?: string; title: string; content: string; category?: string; shortcut_key?: string }) => {
    const tokens = authService.getTokens();
    if (!tokens?.access_token) {
      notify.error('Authentication required');
      return;
    }
    const isEdit = !!data.id;
    const response = await fetch('/api/v1/work-items/canned-responses', {
      method: isEdit ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokens.access_token}`,
      },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to save response');
    }
    notify.success(isEdit ? 'Response updated' : 'Response created');
    // Reload list
    loadCannedResponses();
  };

  // Delete a canned response
  const handleDeleteCannedResponse = async (id: string) => {
    const tokens = authService.getTokens();
    if (!tokens?.access_token) {
      notify.error('Authentication required');
      return;
    }
    const response = await fetch(`/api/v1/work-items/canned-responses?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
      },
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to delete response');
    }
    notify.success('Response deleted');
    // Reload list
    loadCannedResponses();
  };

  // Get color scheme for canned response category
  const getCategoryColor = (category: string): { bg: string; text: string } => {
    const colors: Record<string, { bg: string; text: string }> = {
      greeting: { bg: '#dcfce7', text: '#166534' },      // green
      support: { bg: '#dbeafe', text: '#1e40af' },       // blue
      resolution: { bg: '#fae8ff', text: '#86198f' },    // pink/purple
      info: { bg: '#fef3c7', text: '#92400e' },          // amber
      scheduling: { bg: '#e0e7ff', text: '#3730a3' },    // indigo
      closing: { bg: '#f3e8ff', text: '#7c3aed' },       // violet
      technical: { bg: '#fee2e2', text: '#991b1b' },     // red
      billing: { bg: '#ffedd5', text: '#c2410c' },       // orange
    };
    return colors[category.toLowerCase()] || { bg: '#f3f4f6', text: '#374151' }; // gray default
  };

  // Insert canned response into body — at the START, before signature/quoted content
  const insertCannedResponse = (content: string) => {
    // Replace variable placeholders with actual values
    let processedContent = content;

    // Get current user info
    const userInfo = authService.getUser?.() || null;
    const agentName = userInfo
      ? `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() || userInfo.email || 'Agent'
      : 'Agent';

    // Replace common variables
    processedContent = processedContent
      .replace(/\{\{agent\.name\}\}/gi, agentName)
      .replace(/\{\{date\.today\}\}/gi, new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }));

    // Insert at the START of the editor content (before signature and quoted reply)
    if (bodyEditorRef.current) {
      const currentContent = instance.body || '';
      // Insert before existing content so the response appears at the top of the typing area
      const newContent = processedContent + currentContent;
      onUpdate({ body: newContent, bodyText: newContent.replace(/<[^>]*>/g, ''), isDirty: true });
      // Focus the editor at the start and update content
      bodyEditorRef.current.setContent?.(newContent);
      bodyEditorRef.current.focusAtStart?.();
    }

    setShowCannedResponses(false);
  };

  // Generate AI-suggested response based on email context
  const generateAiSuggestion = async () => {
    if (!instance.replyTo?.email) {
      notify.error('AI suggestions work best when replying to an email');
      return;
    }

    setGeneratingAiSuggestion(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        notify.error('Authentication required');
        return;
      }

      const originalEmail = instance.replyTo.email;
      const replyFrom = originalEmail.from;
      const emailContext = {
        subject: originalEmail.subject || instance.subject,
        from: originalEmail.from,
        body: getEmailBodyAsHtml(originalEmail).replace(/<[^>]*>/g, '').slice(0, 2000), // Text only, truncated
        customerName: replyFrom?.name || replyFrom?.address?.split('@')[0] || replyFrom?.email?.split('@')[0] || 'Customer'
      };

      const response = await fetch('/api/v1/ai/suggest-response', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          emailContext,
          tone: 'professional',
          includeGreeting: true,
          includeClosing: true,
          currentDraft: instance.body || ''
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.suggestion) {
          // Insert the AI suggestion BEFORE the signature, not at the end
          if (bodyEditorRef.current) {
            const currentContent = instance.body || '';
            const sigMarker = 'data-email-signature="true"';
            const sigIdx = currentContent.indexOf(sigMarker);
            let newContent: string;
            if (sigIdx !== -1) {
              // Find the opening <div before the signature marker
              const divStart = currentContent.lastIndexOf('<div', sigIdx);
              if (divStart !== -1) {
                newContent = currentContent.slice(0, divStart) + data.suggestion + currentContent.slice(divStart);
              } else {
                newContent = currentContent + data.suggestion;
              }
            } else {
              // No signature marker — insert before quoted reply content if present
              const quoteMarker = 'data-email-content="true"';
              const quoteIdx = currentContent.indexOf(quoteMarker);
              if (quoteIdx !== -1) {
                const divStart = currentContent.lastIndexOf('<div', quoteIdx);
                if (divStart !== -1) {
                  newContent = currentContent.slice(0, divStart) + data.suggestion + currentContent.slice(divStart);
                } else {
                  newContent = data.suggestion + currentContent;
                }
              } else {
                newContent = currentContent + data.suggestion;
              }
            }
            onUpdate({ body: newContent, bodyText: newContent.replace(/<[^>]*>/g, ''), isDirty: true });
            bodyEditorRef.current.focus?.();
            bodyEditorRef.current.setContent?.(newContent);
          }
          notify.success('✨ AI suggestion inserted');
        } else {
          notify.error(data.error || 'Failed to generate suggestion');
        }
      } else {
        notify.error('Failed to generate AI suggestion');
      }
    } catch (err) {
      console.error('AI suggestion error:', err);
      notify.error('Failed to generate suggestion');
    } finally {
      setGeneratingAiSuggestion(false);
    }
  };

  // Load canned responses when panel opens
  useEffect(() => {
    if (showCannedResponses && cannedResponses.length === 0) {
      loadCannedResponses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: load once when panel opens (load fn not stable)
  }, [showCannedResponses]);

  // Check total-vision-ollama availability on mount — hide AI button if down
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) return;
        const res = await fetch('/api/v1/ai/vision-health', {
          headers: { Authorization: `Bearer ${tokens.access_token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.success && data?.available) {
          setAiAvailable(true);
        }
      } catch {
        // silently hide AI button if health check fails
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load contacts when picker tab changes
  useEffect(() => {
    if (showContactPicker) {
      if (contactPickerTab === 'crm' && crmContacts.length === 0) {
        loadCrmContacts();
      } else if (contactPickerTab === 'personal' && personalContacts.length === 0) {
        loadPersonalContacts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: tab/open driven loads (load fns not stable)
  }, [showContactPicker, contactPickerTab]);

  // Track the initial From address to detect changes
  const initialFromEmailRef = useRef<string | null>(null);
  // Track whether the initial signature fetch has completed (so reply body init can wait)
  const signatureLoadedRef = useRef(false);

  // Load email signature - reload when From address changes
  useEffect(() => {
    const loadSignature = async () => {
      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) {
          console.log('[Composer] No auth token for signature');
          signatureLoadedRef.current = true;
          setSignature(prev => prev); // trigger re-render so reply init can proceed
          return;
        }

        // Get the current "From" email address to show in signature
        // Only use totalhub.online if explicitly selected, otherwise use the display address
        const fromEmail = instance.fromAccount?.emailAddress || '';

        // Track if this is the first load or a change
        const isFromAddressChange = initialFromEmailRef.current !== null && initialFromEmailRef.current !== fromEmail;

        const res = await fetch('/api/v1/email-signature', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.access_token}`
          },
          body: JSON.stringify({
            action: 'getSignature',
            fromEmail: fromEmail // Pass the From address to use in signature
          })
        });

        const data = await res.json();
        console.log('[Composer] Signature API response:', { success: data.success, hasHtml: !!data.html, htmlLength: data.html?.length, fromEmail, isFromAddressChange });
        signatureLoadedRef.current = true;
        if (data.success && data.html) {
          setSignature(data.html);

          // Update signature in editor when From address changed (swap signature)
          if (isFromAddressChange && bodyEditorRef.current?.updateSignature) {
            console.log('[Composer] Swapping signature for From address change');
            bodyEditorRef.current.updateSignature(data.html);
          }
        } else {
          // Signature disabled or empty — trigger re-render so reply init can proceed
          setSignature(prev => prev || '');
        }

        // Update the ref after first successful load
        if (initialFromEmailRef.current === null) {
          initialFromEmailRef.current = fromEmail;
        } else if (isFromAddressChange) {
          initialFromEmailRef.current = fromEmail;
        }
      } catch (err) {
        console.error('[Composer] Failed to load signature:', err);
        signatureLoadedRef.current = true;
        setSignature(prev => prev || ''); // trigger re-render so reply init proceeds
      }
    };
    loadSignature();
  }, [instance.fromAccount?.emailAddress]); // Reload signature when From address changes

  // Initialize reply/forward content on first load
  // Wait for signature to load before initializing so the signature is included in the body
  useEffect(() => {
    if (!instance.replyTo?.email) return;
    // Only skip if body already contains the original email quote
    if (instance.body && instance.body.includes('data-email-content="true"')) return;
    if (!signatureLoadedRef.current) return;

    const email = instance.replyTo.email;
    const replyAll = instance.replyTo.all;

    const signatureHtml = signature
      ? `<div data-email-signature="true" style="margin-top: 16px; padding-top: 16px;">${signature}</div>`
      : '';

    if (instance.replyTo.forward) {
      const forwardedAttachments = email.attachments?.filter((att) => !att.inline) || [];
      const toRecipients = parseEmailRecipients(email.to);
      const toEmails = toRecipients.map((t) => t.email || t.address).filter(Boolean).join(', ');
      onUpdate({
        subject: `Fwd: ${email.subject || ''}`,
        body: `<p><br></p>${signatureHtml}<div data-email-content="true"><hr><p><strong>---------- Forwarded message ----------</strong></p>
            <p><strong>From:</strong> ${email.from?.name || ''} &lt;${email.from?.email || ''}&gt;</p>
            <p><strong>Date:</strong> ${new Date(email.date).toLocaleString()}</p>
            <p><strong>Subject:</strong> ${email.subject || ''}</p>
            <p><strong>To:</strong> ${toEmails}</p>
            ${buildSecurityBannerHtml(email)}
            <br>${getEmailBodyAsHtml(email)}</div>`,
        forwardedAttachments: forwardedAttachments.length > 0 ? forwardedAttachments : undefined,
      });
      return;
    }

    let cancelled = false;

    void (async () => {
      if (client?.id) {
        await hydrateEmailConnectPrefs(client.id);
      }
      if (cancelled) return;

      const rawFromEmail = email.from?.email || email.from?.address || '';
      const fromContact = resolveToContact(rawFromEmail, email.from?.name);
      const emailDate = email.date ? new Date(email.date).toLocaleString() : 'Unknown date';

      const originalEmailQuote = `
<div data-email-content="true">
  <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
    <p style="margin: 0 0 10px 0; color: #6b7280; font-size: 12px;">On ${emailDate}, <strong>${fromContact.name}</strong> &lt;${fromContact.email}&gt; wrote:</p>
    ${buildSecurityBannerHtml(email)}
    <blockquote style="border-left: 3px solid #d1d5db; padding-left: 12px; margin: 0; color: #4b5563;">
      ${getEmailBodyAsHtml(email)}
    </blockquote>
  </div>
</div>`;

      const updates: Partial<ComposerInstance> = {
        to: fromContact.formatted,
        subject: `Re: ${email.subject?.replace(/^Re: /i, '') || ''}`,
        body: `<p><br></p>${signatureHtml}${originalEmailQuote}`,
      };

      const prefs = getEmailConnectPrefsSnapshot();
      const includeAttachments = prefs.includeAttachmentsOnReply !== false;
      if (includeAttachments) {
        const replyAttachments = email.attachments?.filter((att) => !att.inline) || [];
        if (replyAttachments.length > 0) {
          updates.forwardedAttachments = replyAttachments;
        }
      }

      if (replyAll) {
        const myEmails = new Set<string>();
        availableFromAccounts.forEach((acc) => {
          if (acc.emailAddress) myEmails.add(acc.emailAddress.toLowerCase());
        });
        const { primaryAccount: storePrimary } = useEmailStore.getState();
        if (storePrimary?.email) myEmails.add(storePrimary.email.toLowerCase());

        const shouldExclude = (emailAddr: string) => {
          const lower = emailAddr.toLowerCase();
          return lower === fromContact.email.toLowerCase() || myEmails.has(lower);
        };

        const toRecipientsList = parseEmailRecipients(email.to);
        const resolvedToRecipients = toRecipientsList
          .map((t) => {
            const addr = t.email || t.address || '';
            if (!addr) return null;
            const contact = resolveToContact(addr, t.name);
            return contact;
          })
          .filter((c): c is NonNullable<typeof c> => c !== null && !!c.email && !shouldExclude(c.email));

        const ccRecipientsList = parseEmailRecipients(email.cc);
        const resolvedCcRecipients = ccRecipientsList
          .map((t) => {
            const addr = t.email || t.address || '';
            if (!addr) return null;
            const contact = resolveToContact(addr, t.name);
            return contact;
          })
          .filter((c): c is NonNullable<typeof c> => c !== null && !!c.email && !shouldExclude(c.email));

        const seenEmails = new Set<string>();
        seenEmails.add(fromContact.email.toLowerCase());

        const allCcContacts: Array<{ formatted: string }> = [];
        for (const contact of [...resolvedToRecipients, ...resolvedCcRecipients]) {
          const lower = contact.email.toLowerCase();
          if (!seenEmails.has(lower)) {
            seenEmails.add(lower);
            allCcContacts.push(contact);
          }
        }

        const ccList = allCcContacts.map((c) => c.formatted).join(', ');
        if (ccList) {
          updates.cc = ccList;
        }
      }

      if (!cancelled) {
        onUpdate(updates);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Note: Signature is included directly in the body HTML when available.
    // If signature loads after body is set, RichTextEditor's signature effect handles injection.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reply init: expanding deps causes redundant onUpdate loops
  }, [instance.replyTo, signature, availableFromAccounts, client?.id]);

  const saveDraft = useCallback(async () => {
    const htmlBody = bodyEditorRef.current?.getHTML() || instance.body;
    const textBody = bodyEditorRef.current?.getText() || instance.bodyText;
    if (!composerHasPersistableContent(instance, htmlBody, textBody)) {
      return;
    }
    await persistComposerDraftToStorageAndOfflineDb({
      instance,
      htmlBody,
      textBody,
      availableFromAccounts,
      onUpdate,
    });
  }, [instance, onUpdate, availableFromAccounts]);

  // Auto-save
  useEffect(() => {
    if (!instance.isDirty) return;

    const timer = setTimeout(() => {
      saveDraft();
    }, AUTO_SAVE_INTERVAL);

    return () => clearTimeout(timer);
  }, [instance.to, instance.cc, instance.bcc, instance.subject, instance.body, instance.isDirty, saveDraft]);

  // Load suggestions from local storage + server API
  const loadSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // First, show cached suggestions immediately
    const recentEmails = readStoredArray<EmailSuggestion>(RECENT_RECIPIENTS_STORAGE_KEY);
    const companyUsers = readStoredArray<EmailSuggestion>(COMPANY_EMAIL_USERS_STORAGE_KEY);

    const cachedSuggestions = [...recentEmails, ...companyUsers];
    const filtered = cachedSuggestions.filter(
      (s) =>
        s.email.toLowerCase().includes(query.toLowerCase()) ||
        s.name?.toLowerCase().includes(query.toLowerCase())
    );

    const unique = filtered.filter(
      (s, i, arr) => arr.findIndex((x) => x.email === s.email) === i
    );

    setSuggestions(unique.slice(0, 8));
    setShowSuggestions(unique.length > 0);
    setSelectedSuggestionIndex(-1);

    // Then fetch from server for more comprehensive results
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ action: 'searchAllContacts', query })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.length > 0) {
          // Merge server results with local results
          const serverResults: EmailSuggestion[] = (data.data as ContactPickerContact[]).map((contact) => ({
            email: contact.email,
            name: contact.name,
            source: 'crm',
            avatar_url: contact.avatar_url || contact.photo_url,
            job_title: contact.job_title,
            contact_type: contact.contact_type,
            company: contact.company
          }));

          // Combine and dedupe
          const all = [...unique, ...serverResults];
          const deduped = all.filter(
            (s, i, arr) => arr.findIndex((x) => x.email.toLowerCase() === s.email.toLowerCase()) === i
          );

          setSuggestions(deduped.slice(0, 10));
          setShowSuggestions(deduped.length > 0);
        }
      }
    } catch (err) {
      console.log('[Composer] Server search failed, using cached only:', err);
    }
  }, []);

  const saveRecentRecipient = (email: string, name?: string, contact_type?: string, source?: string) => {
    const recent = readStoredArray<EmailSuggestion>(RECENT_RECIPIENTS_STORAGE_KEY);
    const existing = recent.findIndex((r) => r.email === email);
    if (existing >= 0) recent.splice(existing, 1);
    recent.unshift({
      email,
      name,
      source: (source as EmailSuggestion['source']) || 'recent',
      contact_type: contact_type
    });
    localStorage.setItem(RECENT_RECIPIENTS_STORAGE_KEY, JSON.stringify(recent.slice(0, 50)));
  };

  // Auto-save recipient to personal contacts after successful email send
  const autoSaveToPersonalContacts = async (email: string, name?: string) => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          action: 'autoSaveContact',
          email: email.toLowerCase(),
          name
        })
      });
    } catch (err) {
      console.log('[Composer] Failed to auto-save contact:', err);
    }
  };

  const selectSuggestion = (suggestion: EmailSuggestion) => {
    if (activeField) {
      const current = instance[activeField];
      const parts = current.split(',').map((p) => p.trim());
      parts[parts.length - 1] = suggestion.name
        ? `${suggestion.name} <${suggestion.email}>`
        : suggestion.email;
      onUpdate({ [activeField]: parts.join(', ') + ', ', isDirty: true });

      // Save to recent with contact_type if available (for CRM contacts)
      saveRecentRecipient(suggestion.email, suggestion.name, suggestion.contact_type, suggestion.source);
    }
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
  };

  const handleFieldChange = (field: 'to' | 'cc' | 'bcc', value: string) => {
    onUpdate({ [field]: value, isDirty: true });
    const parts = value.split(',');
    const currentPart = parts[parts.length - 1].trim();
    loadSuggestions(currentPart);
    setActiveField(field);
  };

  // Keyboard navigation for suggestions and Tab handling
  const handleFieldKeyDown = (e: React.KeyboardEvent, field: 'to' | 'cc' | 'bcc') => {
    // Handle Tab key - move to next visible field
    if (e.key === 'Tab' && !e.shiftKey) {
      // If suggestions are showing and one is selected, select it first
      if (showSuggestions && suggestions.length > 0 && selectedSuggestionIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[selectedSuggestionIndex]);
        return;
      }

      // Navigate to next visible field
      e.preventDefault();
      setShowSuggestions(false);

      if (field === 'to') {
        // From To: go to Cc (if visible), then Bcc (if visible), then Subject
        if (showCc) {
          ccInputRef.current?.focus();
        } else if (showBcc) {
          bccInputRef.current?.focus();
        } else {
          subjectInputRef.current?.focus();
        }
      } else if (field === 'cc') {
        // From Cc: go to Bcc (if visible), then Subject
        if (showBcc) {
          bccInputRef.current?.focus();
        } else {
          subjectInputRef.current?.focus();
        }
      } else if (field === 'bcc') {
        // From Bcc: go to Subject
        subjectInputRef.current?.focus();
      }
      return;
    }

    // Handle Shift+Tab - move to previous visible field
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      setShowSuggestions(false);

      if (field === 'bcc') {
        // From Bcc: go to Cc (if visible), then To
        if (showCc) {
          ccInputRef.current?.focus();
        } else {
          toInputRef.current?.focus();
        }
      } else if (field === 'cc') {
        // From Cc: go to To
        toInputRef.current?.focus();
      }
      // From To: let default behavior go to previous element
      return;
    }

    // Only handle suggestion navigation if suggestions are showing
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          selectSuggestion(suggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  // Handle Tab navigation from Subject field
  const handleSubjectKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      // Forward Tab: focus the body editor (skip toolbar)
      e.preventDefault();
      bodyEditorRef.current?.focus();
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      // Go back to Bcc (if visible), then Cc (if visible), then To
      if (showBcc) {
        bccInputRef.current?.focus();
      } else if (showCc) {
        ccInputRef.current?.focus();
      } else {
        toInputRef.current?.focus();
      }
    }
  };

  // Add selected contacts from picker
  const handleAddSelectedContacts = () => {
    if (selectedContacts.size === 0) return;

    // Use the correct contact list based on current tab
    let contactsList: ContactPickerContact[];
    let contactSource: string;
    if (contactPickerTab === 'crm') {
      contactsList = crmContacts;
      contactSource = 'crm';
    } else if (contactPickerTab === 'personal') {
      contactsList = personalContacts;
      contactSource = 'personal';
    } else {
      contactsList = companyContacts;
      contactSource = 'company';
    }

    const contacts = contactsList.filter(c => selectedContacts.has(c.email));
    const formatted = contacts.map(c => {
      const name = c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : c.name;
      return name ? `${name} <${c.email}>` : c.email;
    });

    // Save each contact to recent recipients with their contact_type for later lookup
    contacts.forEach(c => {
      const name = c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : c.name;
      saveRecentRecipient(c.email, name, c.contact_type, contactSource);
    });

    const currentTo = instance.to ? instance.to.trim() : '';
    const newTo = currentTo
      ? `${currentTo}${currentTo.endsWith(',') ? ' ' : ', '}${formatted.join(', ')}`
      : formatted.join(', ');

    onUpdate({ to: newTo, isDirty: true });
    setShowContactPicker(false);
    setSelectedContacts(new Set());
    setContactSearch('');
  };

  // Delete personal contacts (supports multiple)
  const handleDeletePersonalContacts = async (contactIds: string[]) => {
    if (contactIds.length === 0) return;

    // Show confirmation
    const confirmMessage = contactIds.length === 1
      ? 'Are you sure you want to delete this contact?'
      : `Are you sure you want to delete ${contactIds.length} contacts?`;

    const confirmed = await notify.confirm({
      title: 'Delete Contacts',
      message: confirmMessage,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      // Delete all selected contacts
      let successCount = 0;
      for (const contactId of contactIds) {
        const response = await fetch('/api/v1/email-contacts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.access_token}`
          },
          body: JSON.stringify({ action: 'deleteMyContact', contactId })
        });

        if (response.ok) {
          successCount++;
        }
      }

      if (successCount > 0) {
        // Remove from local state
        setPersonalContacts(prev => prev.filter(c => !contactIds.includes(String(c.id))));
        // Clear selection
        setSelectedContacts(new Set());
        notify.success(`${successCount} contact${successCount > 1 ? 's' : ''} deleted`);
      }
    } catch (err) {
      console.error('Failed to delete personal contacts:', err);
      notify.error('Failed to delete contacts');
    }
  };

  // Toggle contact selection
  const toggleContactSelection = (email: string) => {
    setSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  // Filter contacts for picker based on selected tab
  const filteredContacts = useMemo(() => {
    // Get the appropriate contacts list based on tab
    let contactsList: ContactPickerContact[];
    if (contactPickerTab === 'crm') {
      contactsList = crmContacts;
      // Apply CRM type filter
      if (crmContactTypeFilter !== 'all') {
        contactsList = contactsList.filter((c) =>
          c.contact_type?.toLowerCase() === crmContactTypeFilter.toLowerCase()
        );
      }
    } else if (contactPickerTab === 'personal') {
      contactsList = personalContacts;
    } else {
      contactsList = companyContacts;
    }

    if (!contactSearch) return contactsList;
    const q = contactSearch.toLowerCase();
    return contactsList.filter((c) =>
      c.email?.toLowerCase().includes(q) ||
      c.first_name?.toLowerCase().includes(q) ||
      c.last_name?.toLowerCase().includes(q) ||
      c.name?.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q) ||
      c.job_title?.toLowerCase().includes(q)
    );
  }, [companyContacts, crmContacts, personalContacts, contactPickerTab, contactSearch, crmContactTypeFilter]);

  // Get unique CRM contact types for filter dropdown
  const crmContactTypes = useMemo(() => {
    const types = new Set<string>();
    crmContacts.forEach((c) => {
      if (c.contact_type) {
        types.add(c.contact_type.toLowerCase());
      }
    });
    return Array.from(types).sort();
  }, [crmContacts]);

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    onUpdate({ attachments: [...instance.attachments, ...files], isDirty: true });
  };

  const handleRemoveAttachment = (index: number) => {
    onUpdate({
      attachments: instance.attachments.filter((_, i) => i !== index),
      isDirty: true
    });
  };

  const parseAddresses = (str: string): EmailAddress[] => {
    return str
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s)
      .map((email) => {
        const match = email.match(/^(.+?)\s*<(.+)>$/);
        if (match) {
          return { name: match[1].trim(), email: match[2].trim() };
        }
        return { email };
      });
  };

  const doSend = async () => {
    setSending(true);

    // Get outbox actions for optimistic update
    const { addToOutbox, markOutboxItemSent, markOutboxItemFailed, primaryAccount } = useEmailStore.getState();

    // Parse recipients
    const toAddresses = parseAddresses(instance.to);
    const ccAddresses = instance.cc.trim() ? parseAddresses(instance.cc) : undefined;
    const bccAddresses = instance.bcc.trim() ? parseAddresses(instance.bcc) : undefined;

    // Create optimistic outbox item IMMEDIATELY
    const outboxTempId = addToOutbox({
      id: `temp-${Date.now()}`,
      queueId: `temp-${Date.now()}`,
      status: 'queued',
      to: toAddresses,
      cc: ccAddresses,
      bcc: bccAddresses,
      subject: instance.subject,
      body: instance.bodyText,
      html: instance.body,
      queuedAt: new Date().toISOString(),
      from: instance.fromAccount?.isShared
        ? { email: instance.fromAccount.emailAddress, name: instance.fromAccount.displayName }
        : primaryAccount
          ? { email: primaryAccount.email, name: primaryAccount.name }
          : undefined,
      sharedMailboxId: instance.fromAccount?.isShared ? instance.fromAccount.mailboxId : null,
      canCancel: true,
    });

    try {
      parseAddresses(instance.to).forEach((addr) => saveRecentRecipient(addr.email, addr.name));
      parseAddresses(instance.cc).forEach((addr) => saveRecentRecipient(addr.email, addr.name));

      const emailData: SendEmailRequest = {
        to: toAddresses,
        subject: instance.subject,
        body: instance.bodyText,
        html: instance.body,
        // Enable queue-based sending for instant response
        useQueue: true,
        priority: 3,
        // Include scheduled send time if set
        ...(instance.scheduledAt && { scheduledAt: instance.scheduledAt.toISOString() }),
      };

      if (ccAddresses) emailData.cc = ccAddresses;
      if (bccAddresses) emailData.bcc = bccAddresses;

      if (instance.attachments.length > 0) {
        const attachmentPromises = instance.attachments.map(async (file) => {
          const base64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              resolve(result.split(',')[1]);
            };
            reader.readAsDataURL(file);
          });
          return {
            filename: file.name,
            content: base64,
            contentType: file.type,
            size: file.size,
          };
        });
        emailData.attachments = await Promise.all(attachmentPromises);
      }

      // Include forwarded attachments (from original email being forwarded)
      if (instance.forwardedAttachments && instance.forwardedAttachments.length > 0) {
        const existingAttachments = emailData.attachments || [];
        // Forward original attachments by their URL/storage_token (server will fetch them)
        const forwardedAtts = instance.forwardedAttachments.map(att => ({
          filename: att.filename,
          contentType: att.contentType,
          size: att.size,
          url: att.url || att.directUrl,
          storage_token: att.storage_token,
          // Mark as forwarded so server knows to fetch content
          isForwarded: true,
        }));
        emailData.attachments = [...existingAttachments, ...forwardedAtts];
      }

      if (instance.replyTo?.email && !instance.replyTo.forward) {
        emailData.inReplyTo = instance.replyTo.email.messageId;
        emailData.references = instance.replyTo.email.references;
      }

      // Add mailbox/connector info for proper SMTP selection
      if (instance.fromAccount?.mailboxId) {
        emailData.mailboxId = instance.fromAccount.mailboxId;
        emailData.fromAddress = instance.fromAccount.emailAddress;
        emailData.fromName = instance.fromAccount.displayName;

        // If sending from connector, include connector ID
        if (instance.fromAccount.isConnector && instance.fromAccount.connectorId) {
          emailData.connectorId = instance.fromAccount.connectorId;
        }
      }

      // Add shared mailbox info if sending from shared mailbox
      if (instance.fromAccount?.isShared && instance.fromAccount?.mailboxId) {
        emailData.sharedMailboxId = instance.fromAccount.mailboxId;
        emailData.sendAs = instance.sendAs;
        emailData.sendOnBehalf = instance.sendOnBehalf;
      }

      const response = await emailApiService.sendEmail(emailData);
      if (response.success) {
        // Remove draft from localStorage
        const drafts = readStoredDrafts();
        delete drafts[instance.id];
        writeStoredDrafts(drafts);

        void (async () => {
          try {
            const { offlineEmail } = await import('@/lib/offlineEmail');
            await offlineEmail.deleteDraft(instance.draftMessageId || instance.id);
          } catch {
            /* ignore */
          }
        })();

        // Delete draft thread from server if we had a draft message ID
        // Note: Draft may only exist locally (e.g., when using connector sending),
        // so 404 errors are expected and silently ignored
        if (instance.draftMessageId) {
          try {
            const deleteResult = await emailApiService.deleteThread(instance.draftMessageId, true);
            if (deleteResult.success) {
              console.log('[Composer] Draft thread deleted:', instance.draftMessageId);
            }
            // If not successful but no exception, draft didn't exist on server - that's OK
          } catch (draftDeleteError: unknown) {
            // 404 is expected when draft was never synced to server (local-only draft)
            const is404 = isStatusLikeError(draftDeleteError) && (
              draftDeleteError.message?.includes('404') ||
              draftDeleteError.status === 404 ||
              draftDeleteError.response?.status === 404
            );
            if (!is404) {
              console.warn('[Composer] Failed to delete draft thread:', draftDeleteError);
            }
          }
        }

        // Invalidate Drafts and Sent folder caches so they reflect the change
        {
          const accountIdForCache = instance.fromAccount?.mailboxId || 0;
          const draftsCacheKey = `Drafts:${accountIdForCache}`;
          const sentCacheKey = `Sent:${accountIdForCache}`;
          const state = useEmailStore.getState();
          useEmailStore.setState({
            folderThreadsLastSync: {
              ...state.folderThreadsLastSync,
              [draftsCacheKey]: 0,
              [sentCacheKey]: 0,
            },
          });
          // Also clear Drafts from memory cache so stale draft doesn't reappear
          const updatedFolderThreads = { ...state.folderThreads };
          delete updatedFolderThreads[draftsCacheKey];
          useEmailStore.setState({ folderThreads: updatedFolderThreads });
          console.log('[Composer] Invalidated Drafts and Sent folder caches after send');
        }

        const isQueuedSend = !!response.data?.queued;

        // Update outbox item with real queue ID and keep it in background-processing state
        if (isQueuedSend && response.data?.queueId) {
          // Play subtle "email sent" sound for confirmation
          desktopNotifications.playEmailSentSound();

          // Queued - update with real queueId, status stays 'queued' or 'sending'
          const { updateOutboxItem } = useEmailStore.getState();
          updateOutboxItem(outboxTempId, {
            queueId: response.data.queueId,
            status: instance.scheduledAt ? 'scheduled' : 'sending',
            isOptimistic: false,
          });

          if (instance.scheduledAt) {
            const scheduledDate = new Date(instance.scheduledAt);
            notify.success(`Email scheduled for ${scheduledDate.toLocaleDateString()} at ${scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, { duration: 4000 });
          }
        } else {
          // Sent immediately
          desktopNotifications.playEmailSentSound();
          markOutboxItemSent(outboxTempId, response.data?.messageId);
        }

        // Auto-save recipients to personal contacts (fire and forget)
        toAddresses.forEach(addr => autoSaveToPersonalContacts(addr.email, addr.name));
        ccAddresses?.forEach(addr => autoSaveToPersonalContacts(addr.email, addr.name));

        if (isQueuedSend) {
          // Close the composer without triggering sent-only optimistic UI flows.
          // Final delivery state will arrive through outbox/websocket queue updates.
          onClose();
        } else {
          // Create sent email data for optimistic update only after an actual immediate send
          const sentEmailData: SentEmailData = {
            messageId: response.data?.messageId || `sent-${Date.now()}`,
            to: toAddresses,
            cc: ccAddresses,
            bcc: bccAddresses,
            subject: instance.subject,
            body: instance.bodyText,
            html: instance.body,
            date: new Date(),
            fromAccount: instance.fromAccount ?? undefined,
          };

          onSent(sentEmailData);
        }
      } else {
        // Mark outbox item as failed
        markOutboxItemFailed(outboxTempId, response.error || 'Failed to send email');
        notify.error(response.error || 'Failed to send email');
      }
    } catch (error: unknown) {
      console.error('Error sending email:', error);
      const errorMessage = getErrorMessage(error, 'Failed to send email');
      // Mark outbox item as failed
      markOutboxItemFailed(outboxTempId, errorMessage);
      notify.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!instance.to.trim()) return;

    // Check for attachment mention without attachments
    // Remove signature and disclaimer before checking to avoid false positives
    let bodyTextForCheck = (instance.body + ' ' + instance.bodyText).toLowerCase();
    // Remove email signature div and everything after it
    bodyTextForCheck = bodyTextForCheck.replace(/<div[^>]*class="[^"]*email-signature[^"]*"[^>]*>.*$/is, '');
    // Remove disclaimer sections (common phrases)
    bodyTextForCheck = bodyTextForCheck.replace(/this email and any attachments are confidential.*$/is, '');
    bodyTextForCheck = bodyTextForCheck.replace(/if you have received this.*$/is, '');
    bodyTextForCheck = bodyTextForCheck.replace(/any unauthorized review.*$/is, '');

    const mentionsAttachment = /\b(attach(ed|ment|ments|ing)?|enclosed|enclosing)\b/i.test(bodyTextForCheck);

    const hasAnyAttachments = instance.attachments.length > 0 || (instance.forwardedAttachments?.length || 0) > 0;

    if (mentionsAttachment && !hasAnyAttachments) {
      return new Promise<void>((resolve) => {
        notify.confirm({
          title: 'No Attachments Found',
          message: 'Your email mentions attachments, but no files are attached. Do you still want to send?',
          confirmText: 'Send Anyway',
          cancelText: 'Go Back',
          variant: 'warning',
          onConfirm: async () => {
            await doSend();
            resolve();
          },
          onCancel: () => {
            resolve();
          }
        });
      });
    }

    await doSend();
  };

  // Select from account
  const handleSelectFromAccount = (account: FromAccount) => {
    // Default to sendAs if available, otherwise sendOnBehalf
    const sendAs = account.isShared && account.permissions?.canSendAs;
    const sendOnBehalf = account.isShared && !sendAs && account.permissions?.canSendOnBehalf;
    onUpdate({
      fromAccount: account,
      sendAs,
      sendOnBehalf,
      isDirty: true
    });
    setShowFromDropdown(false);
  };

  const handleClose = async () => {
    const htmlBody = bodyEditorRef.current?.getHTML() || instance.body;
    const textBody = bodyEditorRef.current?.getText() || instance.bodyText;
    if (composerHasPersistableContent(instance, htmlBody, textBody)) {
      await persistComposerDraftToStorageAndOfflineDb({
        instance,
        htmlBody,
        textBody,
        availableFromAccounts,
        onUpdate,
      });
    }
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Generate ICS file for meeting invite
  const generateMeetingICS = (meeting: NonNullable<ComposerInstance['meetingInvite']>) => {
    const formatICSDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@totalaccess`;
    const now = new Date();

    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//TotalAccess//Calendar//EN',
      'METHOD:REQUEST',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${formatICSDate(now)}`,
      `DTSTART:${formatICSDate(meeting.startTime)}`,
      `DTEND:${formatICSDate(meeting.endTime)}`,
      `SUMMARY:${meeting.title}`,
      meeting.location ? `LOCATION:${meeting.location}` : '',
      meeting.description ? `DESCRIPTION:${meeting.description.replace(/\n/g, '\\n')}` : '',
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(Boolean).join('\r\n');

    return ics;
  };

  // Add meeting invite as attachment
  const handleAddMeetingInvite = (meeting: NonNullable<ComposerInstance['meetingInvite']>) => {
    const icsContent = generateMeetingICS(meeting);
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const file = new File([blob], 'meeting-invite.ics', { type: 'text/calendar' });

    onUpdate({
      meetingInvite: meeting,
      attachments: [...instance.attachments, file],
      isDirty: true
    });
    setShowMeetingCreator(false);
  };

  // Only render full composer if active
  if (!isActive) return null;

  const isMobileApp = isCapacitor();
  const isSlideSheet = composePresentation === 'desktop-email-slide';

  const composeTitle = instance.replyTo?.forward
    ? 'Forward'
    : instance.replyTo?.email
      ? 'Reply'
      : 'Compose Email';

  const subtitleParts: string[] = [];
  if (instance.isDirty) subtitleParts.push('Unsaved changes');
  if (instance.scheduledAt) subtitleParts.push('Scheduled');

  const composeHeader = (
    <CapacitorModalHeader
      icon={Send}
      title={composeTitle}
      accentColor={headerBgColor}
      isDark={isDark}
      titleExtra={
        <>
          <EncryptedBadge size="sm" className="shrink-0" />
          <UserDocsLink docsPath="/total-connect/composing-and-sending" label="User guide: Composing & sending" />
        </>
      }
      subtitle={
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          {subtitleParts.length > 0 ? subtitleParts.join(' · ') : 'Encrypted'}
        </span>
      }
      isMobile={isMobile}
      applyTopSafeArea={isMobile}
      actions={
        <>
          {showComposerMaximize && !isMobile && (
            <button
              type="button"
              onClick={onMaximize}
              className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              title={instance.isMaximized ? 'Restore' : 'Maximize'}
            >
              {instance.isMaximized ? (
                <Square className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              ) : (
                <Maximize2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              )}
            </button>
          )}
          {!isMobile && (
            <button
              type="button"
              onClick={onMinimize}
              className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Minimize"
            >
              <Minimize2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Close"
          >
            <X className={cn('text-gray-500 dark:text-gray-400', isMobile ? 'h-5 w-5' : 'h-4 w-4')} />
          </button>
        </>
      }
    />
  );

  const innerSurfaceClass = cn(
    'flex flex-col min-h-0 bg-white shadow-2xl transition-all duration-200 dark:bg-gray-900',
    isMobile
      ? 'h-full max-h-none w-full max-w-none rounded-none'
      : isSlideSheet
        ? 'h-full w-full flex-1 md:max-w-5xl md:flex-none md:animate-slide-in-right md:border-l md:border-gray-200 md:dark:border-gray-700'
        : showComposerMaximize && instance.isMaximized
          ? 'm-0 h-full max-h-none w-full max-w-none rounded-none'
          : 'max-h-[85vh] w-full max-w-4xl rounded-xl',
  );

  const composerFrame = (
    <div className={innerSurfaceClass}>
      {composeHeader}

      {/* Recipients/Subject Accordion - collapsible on mobile to maximize typing area */}
      {isMobile && recipientsCollapsed ? (
        <button
          type="button"
          onClick={() => {
            userCollapsedRecipients.current = false;
            setRecipientsCollapsed(false);
          }}
          className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-left w-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 rotate-90" />
          <div className="flex-1 min-w-0 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <span className="truncate">
              {(() => {
                const toStr = typeof instance.to === 'string' ? instance.to : '';
                const toNames = toStr.split(',').map(s => {
                  const t = s.trim();
                  const angle = t.match(/<([^<>]+@[^<>]+)>/);
                  if (angle) {
                    return t.slice(0, angle.index).trim() || angle[1].trim();
                  }
                  return t;
                }).filter(Boolean).slice(0, 2).join(', ');
                return toNames || 'Recipients';
              })()}
            </span>
            {instance.subject && (
              <span className="truncate text-gray-400 dark:text-gray-500">— {instance.subject}</span>
            )}
          </div>
          <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>
      ) : (
      <div className={cn('border-b border-gray-200 dark:border-gray-700', isMobile && 'relative')}>
        {isMobile && (
          <button
            type="button"
            onClick={() => {
              userCollapsedRecipients.current = !recipientsCollapsed;
              setRecipientsCollapsed(!recipientsCollapsed);
            }}
            className="absolute right-2 top-1 z-10 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            title={recipientsCollapsed ? 'Show recipients' : 'Hide recipients'}
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        )}
        {/* From Account Selector - always show to display sending identity */}
        {availableFromAccounts.length >= 1 && (
          <div className="relative flex items-center px-4 py-2 border-b border-gray-100 dark:border-gray-800" ref={fromDropdownRef}>
            <span className="text-sm text-gray-500 dark:text-gray-400 w-12">From:</span>
            <button
              onClick={() => setShowFromDropdown(!showFromDropdown)}
              className="flex-1 flex items-center justify-between bg-transparent text-sm text-gray-900 dark:text-white p-0 hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-2 py-1"
            >
              <div className="flex flex-col items-start">
                <span className="font-medium">
                  {instance.fromAccount?.displayName || availableFromAccounts[0]?.displayName || 'Select account...'}
                </span>
                {(instance.fromAccount?.emailAddress || availableFromAccounts[0]?.emailAddress) && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    {instance.fromAccount?.emailAddress || availableFromAccounts[0]?.emailAddress}
                    {(instance.fromAccount?.hasDeliveryIssues ?? availableFromAccounts[0]?.hasDeliveryIssues) && (
                      <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-[10px] font-medium">
                        Needs attention
                      </span>
                    )}
                    {(instance.fromAccount?.isConnector ?? availableFromAccounts[0]?.isConnector) && (
                      <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded text-[10px]">
                        Connector
                      </span>
                    )}
                    {getFromAccountStatusLabel(
                      instance.fromAccount || availableFromAccounts[0],
                      instance.sendAs,
                      instance.sendOnBehalf,
                    ) && (
                        <span className="text-blue-600 dark:text-blue-400">
                          ({getFromAccountStatusLabel(
                            instance.fromAccount || availableFromAccounts[0],
                            instance.sendAs,
                            instance.sendOnBehalf,
                          )})
                        </span>
                      )}
                  </span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {showFromDropdown && (
              <div className="absolute top-full left-12 right-4 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                {availableFromAccounts.map((account, index) => (
                  <button
                    key={`${account.mailboxId}-${account.isConnector ? 'connector' : account.isAlias ? `alias-${account.aliasId}` : 'mailbox'}-${index}`}
                    onClick={() => handleSelectFromAccount(account)}
                    className={cn(
                      "flex flex-col items-start w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800",
                      instance.fromAccount?.mailboxId === account.mailboxId &&
                      instance.fromAccount?.emailAddress === account.emailAddress &&
                      "bg-blue-50 dark:bg-blue-900/20"
                    )}
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                      {account.isPartnerAccount ? (
                        <Building2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                      ) : account.isTeamMailbox ? (
                        <Users className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                      ) : null}
                      <span>{account.displayName || account.emailAddress}</span>
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      {account.emailAddress}
                      {account.hasDeliveryIssues && (
                        <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-[10px] font-medium">
                          Needs attention
                        </span>
                      )}
                      {account.isAlias && (
                        <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-[10px]">
                          Alias
                        </span>
                      )}
                      {account.isTeamMailbox && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={{
                            backgroundColor: `${account.teamColor || '#3b82f6'}15`,
                            color: account.teamColor || '#3b82f6',
                          }}
                        >
                          Team: {account.teamName || 'Team'}
                        </span>
                      )}
                      {account.isPartnerAccount && (
                        <span className="px-1.5 py-0.5 bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 rounded text-[10px] font-medium">
                          Partner{account.partnerClientName ? ` • ${account.partnerClientName}` : ''}
                        </span>
                      )}
                      {account.isShared && !account.isTeamMailbox && (
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-[10px]">
                          Shared Mailbox
                          {account.permissions?.canSendAs && ' • Send As'}
                          {account.permissions?.canSendOnBehalf && ' • Send on Behalf'}
                        </span>
                      )}
                      {account.isConnector && (
                        <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded text-[10px]">
                          Connector
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* To Field - Outlook-style chips */}
        <div className="relative flex items-start px-4 py-2 border-b border-gray-100 dark:border-gray-800">
          <span className="text-sm text-gray-500 dark:text-gray-400 w-12 pt-1">To:</span>
          <EmailRecipientsInput
            value={instance.to}
            onChange={(value) => onUpdate({ to: value, isDirty: true })}
            onFocus={() => setActiveField('to')}
            placeholder="recipient@example.com"
            suggestions={suggestions}
            showSuggestions={showSuggestions && activeField === 'to'}
            selectedSuggestionIndex={selectedSuggestionIndex}
            onLoadSuggestions={loadSuggestions}
            onSelectSuggestion={selectSuggestion}
            onKeyDown={(e) => handleFieldKeyDown(e, 'to')}
            inputRef={toInputRef}
          />
          <div className="flex gap-2 text-sm items-center flex-shrink-0 pt-0.5">
            <button
              onClick={() => setShowContactPicker(true)}
              className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              title="Select contacts"
            >
              <UserPlus className="w-4 h-4" />
            </button>
            {!showCc && (
              <button onClick={() => setShowCc(true)} className="text-gray-500 hover:text-gray-700">Cc</button>
            )}
            {!showBcc && (
              <button onClick={() => setShowBcc(true)} className="text-gray-500 hover:text-gray-700">Bcc</button>
            )}
          </div>

          {showSuggestions && activeField === 'to' && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute top-full left-12 right-4 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
            >
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => selectSuggestion(s)}
                  className={cn(
                    "flex items-center gap-3 w-full px-3 py-2 text-left",
                    selectedSuggestionIndex === i
                      ? "bg-blue-50 dark:bg-blue-900/30"
                      : "hover:bg-gray-50 dark:hover:bg-gray-800"
                  )}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                    {s.avatar_url ? (
                      <img src={s.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {s.name && <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>}
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{s.email}</p>
                    {s.job_title && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{s.job_title}</p>}
                  </div>
                  {s.source === 'company' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">
                      Company
                    </span>
                  )}
                </button>
              ))}
              <div className="px-3 py-1.5 text-xs text-gray-400 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                Use ↑↓ arrows to navigate, Enter to select
              </div>
            </div>
          )}
        </div>

        {/* Cc Field - Outlook-style chips */}
        {showCc && (
          <div className="relative flex items-start px-4 py-2 border-b border-gray-100 dark:border-gray-800">
            <span className="text-sm text-gray-500 dark:text-gray-400 w-12 pt-1">Cc:</span>
            <EmailRecipientsInput
              value={instance.cc}
              onChange={(value) => onUpdate({ cc: value, isDirty: true })}
              onFocus={() => setActiveField('cc')}
              suggestions={suggestions}
              showSuggestions={showSuggestions && activeField === 'cc'}
              selectedSuggestionIndex={selectedSuggestionIndex}
              onLoadSuggestions={loadSuggestions}
              onSelectSuggestion={selectSuggestion}
              onKeyDown={(e) => handleFieldKeyDown(e, 'cc')}
              inputRef={ccInputRef}
            />
            {showSuggestions && activeField === 'cc' && suggestions.length > 0 && (
              <div className="absolute top-full left-12 right-4 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => selectSuggestion(s)}
                    className={cn(
                      "flex items-center gap-3 w-full px-3 py-2 text-left",
                      selectedSuggestionIndex === i
                        ? "bg-blue-50 dark:bg-blue-900/30"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800"
                    )}
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <Users className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {s.name && <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>}
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{s.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Bcc Field - Outlook-style chips */}
        {showBcc && (
          <div className="relative flex items-start px-4 py-2 border-b border-gray-100 dark:border-gray-800">
            <span className="text-sm text-gray-500 dark:text-gray-400 w-12 pt-1">Bcc:</span>
            <EmailRecipientsInput
              value={instance.bcc}
              onChange={(value) => onUpdate({ bcc: value, isDirty: true })}
              onFocus={() => setActiveField('bcc')}
              suggestions={suggestions}
              showSuggestions={showSuggestions && activeField === 'bcc'}
              selectedSuggestionIndex={selectedSuggestionIndex}
              onLoadSuggestions={loadSuggestions}
              onSelectSuggestion={selectSuggestion}
              onKeyDown={(e) => handleFieldKeyDown(e, 'bcc')}
              inputRef={bccInputRef}
            />
            {showSuggestions && activeField === 'bcc' && suggestions.length > 0 && (
              <div className="absolute top-full left-12 right-4 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => selectSuggestion(s)}
                    className={cn(
                      "flex items-center gap-3 w-full px-3 py-2 text-left",
                      selectedSuggestionIndex === i
                        ? "bg-blue-50 dark:bg-blue-900/30"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800"
                    )}
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <Users className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {s.name && <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>}
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{s.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center px-4 py-2">
          <span className="text-sm text-gray-500 dark:text-gray-400 w-12">Subject:</span>
          <input
            ref={subjectInputRef}
            type="text"
            value={instance.subject}
            onChange={(e) => onUpdate({ subject: e.target.value, isDirty: true })}
            onKeyDown={handleSubjectKeyDown}
            placeholder="Subject"
            spellCheck={true}
            className="flex-1 bg-transparent border-0 text-sm text-gray-900 dark:text-white focus:ring-0 p-0"
          />
        </div>
      </div>
      )}

      {/* Rich Text Body */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <RichTextEditor
          ref={bodyEditorRef}
          content={instance.body}
          onChange={(html, text) => {
            onUpdate({ body: html, bodyText: text, isDirty: true });
          }}
          placeholder="Write your message..."
          signature={signature || undefined}
          isReplyOrForward={!!instance.replyTo}
          className="flex-1"
        />
      </div>

      {/* Attachments - Rich visual preview with thumbnails */}
      {(instance.attachments.length > 0 || (instance.forwardedAttachments?.length || 0) > 0) && (
        <ComposerAttachmentPreview
          files={instance.attachments}
          forwardedAttachments={instance.forwardedAttachments}
          onRemoveFile={handleRemoveAttachment}
          onRemoveForwarded={(index) => {
            const updated = instance.forwardedAttachments?.filter((_, i) => i !== index);
            onUpdate({ forwardedAttachments: updated?.length ? updated : undefined });
          }}
          messageId={instance.replyTo?.email?.messageId}
          defaultCollapsed={isMobile}
        />
      )}

      {/* Footer */}
      <div
        className={cn(
          'flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800',
          isMobile ? 'px-2 py-2.5' : 'px-4 py-3 rounded-b-xl'
        )}
        style={isMobile ? { paddingBottom: `calc(0.75rem + var(--safe-area-inset-bottom, 0px))` } : undefined}
      >
        <div className={cn("flex items-center", isMobile ? "gap-0.5" : "gap-1")}>
          <input ref={fileInputRef} type="file" multiple onChange={handleAttachFiles} className="hidden" />
          <button
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700",
              isMobile ? "p-2.5" : "p-2"
            )}
            title="Attach files"
          >
            <Paperclip className={cn(isMobile ? "w-[22px] h-[22px]" : "w-5 h-5")} />
          </button>
          <button
            onClick={() => setShowMeetingCreator(true)}
            className={cn(
              "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700",
              isMobile ? "p-2.5" : "p-2"
            )}
            title="Add meeting invite"
          >
            <Calendar className={cn(isMobile ? "w-[22px] h-[22px]" : "w-5 h-5")} />
          </button>
          <button
            onClick={() => setShowScheduler(true)}
            className={cn(
              "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700",
              isMobile ? "p-2.5" : "p-2"
            )}
            title="Schedule send"
          >
            <Clock className={cn(isMobile ? "w-[22px] h-[22px]" : "w-5 h-5")} />
          </button>
          <button
            onClick={() => setShowCannedResponses(!showCannedResponses)}
            className={cn(
              "rounded-lg transition-colors",
              isMobile ? "p-2.5" : "p-2",
              showCannedResponses
                ? "bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400"
                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            )}
            title="Insert canned response"
          >
            <Zap className={cn(isMobile ? "w-[22px] h-[22px]" : "w-5 h-5")} />
          </button>
          {aiAvailable && (
            <button
              onClick={generateAiSuggestion}
              disabled={generatingAiSuggestion}
              className={cn(
                "rounded-lg transition-colors",
                isMobile ? "p-2.5" : "p-2",
                generatingAiSuggestion
                  ? "text-purple-500 dark:text-purple-400"
                  : "text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-200 dark:hover:bg-gray-700"
              )}
              title="AI assistant — draft a reply"
            >
              {generatingAiSuggestion ? (
                <Loader2 className={cn(isMobile ? "w-[22px] h-[22px]" : "w-5 h-5", "animate-spin")} />
              ) : (
                <Sparkles className={cn(isMobile ? "w-[22px] h-[22px]" : "w-5 h-5")} />
              )}
            </button>
          )}

          {instance.lastSavedAt > 0 && (
            <span className="text-xs text-gray-400 ml-2">
              Saved {new Date(instance.lastSavedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className={cn("flex items-center", isMobile ? "gap-1.5" : "gap-2")}>
          <button onClick={handleClose} className={cn(
            "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
            isMobile ? "px-2.5 py-2 text-xs" : "px-4 py-2 text-sm"
          )}>
            {isMobile ? 'Close' : 'Save & Close'}
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !instance.to.trim()}
            className={cn(
              "flex items-center gap-1.5 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors",
              isMobile ? "px-3 py-2 text-sm" : "px-4 py-2 text-sm"
            )}
            style={{ backgroundColor: headerBgColor }}
          >
            {sending ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {instance.scheduledAt ? 'Schedule' : 'Send'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Canned Responses Panel - right-side slide-in on mobile/fullscreen, bottom slider on desktop smaller mode */}
      {showCannedResponses && (() => {
        const useSlideIn = isMobile || (showComposerMaximize && instance.isMaximized) || isSlideSheet;

        if (useSlideIn) {
          return createPortal(
            <>
              <div
                className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300 z-[200]"
                onClick={() => setShowCannedResponses(false)}
              />
              <div
                className="fixed right-0 top-0 bottom-0 flex w-full animate-slide-in-right flex-col shadow-2xl bg-white dark:bg-gray-900 z-[201] max-w-md"
                style={{
                  paddingTop: 'env(safe-area-inset-top)',
                  paddingBottom: 'env(safe-area-inset-bottom)',
                }}
              >
                {/* Header */}
                <div
                  className="flex items-center justify-between px-4 py-3 flex-shrink-0"
                  style={{ backgroundColor: headerBgColor }}
                >
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-white" />
                    <span className="font-semibold text-white">Quick Responses</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingCannedResponse(null);
                        setShowCannedResponseEditor(true);
                      }}
                      className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                      title="New response"
                    >
                      <Plus className="w-5 h-5 text-white" />
                    </button>
                    <button
                      onClick={() => setShowCannedResponses(false)}
                      className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-800">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search responses..."
                      value={cannedResponsesSearch}
                      onChange={(e) => {
                        setCannedResponsesSearch(e.target.value);
                        loadCannedResponses();
                      }}
                      className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* Responses List */}
                <div className="flex-1 overflow-y-auto min-h-0 bg-white dark:bg-gray-900">
                  {loadingCannedResponses ? (
                    <div className="flex items-center justify-center py-12">
                      <span className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : cannedResponses.length === 0 ? (
                    <div className="py-10 text-center text-gray-500 dark:text-gray-400 px-4">
                      <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm font-medium">No canned responses found</p>
                      <p className="text-xs mt-1 opacity-75">Create responses in Support Settings</p>
                    </div>
                  ) : (
                    <div className="p-3 space-y-2">
                      {cannedResponses.map((response) => (
                        <div
                          key={response.id}
                          className="w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 rounded-lg transition-all duration-150 group"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <button
                              className="flex-1 min-w-0 text-left"
                              onClick={() => insertCannedResponse(response.content)}
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white text-sm">
                                  {response.title}
                                </span>
                                {response.category && (
                                  <span
                                    className="flex-shrink-0 px-2 py-0.5 text-[10px] rounded-full uppercase tracking-wider font-bold"
                                    style={{
                                      backgroundColor: getCategoryColor(response.category).bg,
                                      color: getCategoryColor(response.category).text
                                    }}
                                  >
                                    {response.category}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2">
                                {response.content.replace(/<[^>]*>/g, '').slice(0, 120)}
                              </p>
                            </button>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {response.shortcut_key && (
                                <span className="px-2 py-1 text-[11px] font-mono bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-600 group-hover:bg-blue-100 dark:group-hover:bg-blue-800 group-hover:text-blue-700 dark:group-hover:text-blue-300 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
                                  /{response.shortcut_key}
                                </span>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingCannedResponse(response);
                                  setShowCannedResponseEditor(true);
                                }}
                                className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                title="Edit response"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>,
            document.body
          );
        }

        // Desktop smaller mode — bottom slider (constrained to composer width)
        return (
        <div className="fixed bottom-0 left-0 right-0 flex justify-center z-[200]">
        <div className="bg-white dark:bg-gray-900 shadow-[0_-4px_20px_rgba(0,0,0,0.15)] border-t border-gray-200 dark:border-gray-700 max-h-[280px] flex flex-col w-full max-w-4xl rounded-t-xl">
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-2 flex-shrink-0"
            style={{ backgroundColor: headerBgColor }}
          >
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-white" />
              <span className="font-semibold text-white text-sm">Quick Responses</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setEditingCannedResponse(null);
                  setShowCannedResponseEditor(true);
                }}
                className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                title="New response"
              >
                <Plus className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setShowCannedResponses(false)}
                className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search responses..."
                value={cannedResponsesSearch}
                onChange={(e) => {
                  setCannedResponsesSearch(e.target.value);
                  loadCannedResponses();
                }}
                className="w-full pl-9 pr-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
          </div>

          {/* Responses List */}
          <div className="flex-1 overflow-y-auto min-h-0 bg-white dark:bg-gray-900">
            {loadingCannedResponses ? (
              <div className="flex items-center justify-center py-8">
                <span className="w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : cannedResponses.length === 0 ? (
              <div className="py-6 text-center text-gray-500 dark:text-gray-400 px-4">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium">No canned responses found</p>
                <p className="text-xs mt-1 opacity-75">Create responses in Support Settings</p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {cannedResponses.map((response) => (
                  <div
                    key={response.id}
                    className="w-full px-3 py-2.5 text-left bg-gray-50 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 rounded-lg transition-all duration-150 group"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <button
                        className="flex-1 min-w-0 text-left"
                        onClick={() => insertCannedResponse(response.content)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white text-sm">
                            {response.title}
                          </span>
                          {response.category && (
                            <span
                              className="flex-shrink-0 px-2 py-0.5 text-[10px] rounded-full uppercase tracking-wider font-bold"
                              style={{
                                backgroundColor: getCategoryColor(response.category).bg,
                                color: getCategoryColor(response.category).text
                              }}
                            >
                              {response.category}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                          {response.content.replace(/<[^>]*>/g, '').slice(0, 100)}...
                        </p>
                      </button>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {response.shortcut_key && (
                          <span className="px-2 py-1 text-[11px] font-mono bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-600 group-hover:bg-blue-100 dark:group-hover:bg-blue-800 group-hover:text-blue-700 dark:group-hover:text-blue-300 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
                            /{response.shortcut_key}
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCannedResponse(response);
                            setShowCannedResponseEditor(true);
                          }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                          title="Edit response"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
        );
      })()}

      {/* Canned Response Editor Modal */}
      <CannedResponseEditorModal
        isOpen={showCannedResponseEditor}
        onClose={() => {
          setShowCannedResponseEditor(false);
          setEditingCannedResponse(null);
        }}
        onSave={handleSaveCannedResponse}
        onDelete={handleDeleteCannedResponse}
        editingResponse={editingCannedResponse}
        headerBgColor={headerBgColor}
      />

      {/* Contact Picker Modal */}
      {showContactPicker && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120] p-4"
          style={isMobileApp ? {
            top: 'var(--safe-area-inset-top, 0px)',
            bottom: 'var(--safe-area-inset-bottom, 0px)'
          } : undefined}
        >
          <div className={cn(
            "bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg flex flex-col",
            isMobileApp ? 'max-h-full' : 'max-h-[70vh]'
          )}>
            <div
              className="flex items-center justify-between px-4 py-3 border-b rounded-t-xl"
              style={{ backgroundColor: headerBgColor }}
            >
              <h3 className="font-medium text-white flex items-center gap-2">
                <Users className="w-4 h-4" />
                Select Contacts
              </h3>
              <button
                onClick={() => {
                  setShowContactPicker(false);
                  setSelectedContacts(new Set());
                  setContactSearch('');
                }}
                className="p-1.5 text-white/80 hover:text-white rounded-lg hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b dark:border-gray-700">
              <button
                onClick={() => setContactPickerTab('company')}
                className={cn(
                  "flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5",
                  contactPickerTab === 'company'
                    ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                )}
              >
                <Building2 className="w-4 h-4" />
                Team
              </button>
              <button
                onClick={() => setContactPickerTab('crm')}
                className={cn(
                  "flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5",
                  contactPickerTab === 'crm'
                    ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                )}
              >
                <Globe className="w-4 h-4" />
                CRM
              </button>
              <button
                onClick={() => setContactPickerTab('personal')}
                className={cn(
                  "flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5",
                  contactPickerTab === 'personal'
                    ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                )}
              >
                <UserPlus className="w-4 h-4" />
                My Contacts
              </button>
            </div>

            <div className="p-4 border-b dark:border-gray-700">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder={`Search ${contactPickerTab === 'company' ? 'team members' : contactPickerTab === 'crm' ? 'customers & suppliers' : 'personal contacts'}...`}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm"
                    autoFocus
                  />
                </div>
                {/* CRM Type Filter */}
                {contactPickerTab === 'crm' && crmContactTypes.length > 0 && (
                  <select
                    value={crmContactTypeFilter}
                    onChange={(e) => setCrmContactTypeFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm min-w-[120px]"
                  >
                    <option value="all">All Types</option>
                    {crmContactTypes.map((type) => (
                      <option key={type} value={type} className="capitalize">
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {loadingContacts ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-2" />
                  Loading contacts...
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {contactPickerTab === 'personal' ? (
                    <div>
                      <p>No personal contacts yet</p>
                      <p className="text-xs mt-1">Contacts are auto-saved when you send emails</p>
                    </div>
                  ) : (
                    'No contacts found'
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredContacts.map((contact) => {
                    const name = contact.first_name && contact.last_name
                      ? `${contact.first_name} ${contact.last_name}`
                      : contact.name || contact.email;
                    const isSelected = selectedContacts.has(contact.email);

                    // Build tooltip with all available contact info
                    const tooltipParts = [name];
                    if (contact.email) tooltipParts.push(`Email: ${contact.email}`);
                    if (contact.job_title) tooltipParts.push(`Position: ${contact.job_title}`);
                    if (contact.department) tooltipParts.push(`Department: ${contact.department}`);
                    if (contact.company) tooltipParts.push(`Company: ${contact.company}`);
                    if (contact.phone || contact.mobile) tooltipParts.push(`Phone: ${contact.phone || contact.mobile}`);
                    if (contact.contact_type) tooltipParts.push(`Type: ${contact.contact_type}`);
                    const tooltipText = tooltipParts.join('\n');

                    return (
                      <div
                        key={contact.id}
                        className={cn(
                          "flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left transition-colors group",
                          isSelected
                            ? "bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800"
                            : "hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent"
                        )}
                        title={tooltipText}
                      >
                        <button
                          onClick={() => toggleContactSelection(contact.email)}
                          className="flex items-center gap-3 flex-1 min-w-0"
                        >
                          <div className={cn(
                            "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0",
                            isSelected
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-300 dark:border-gray-600"
                          )}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {contact.avatar_url || contact.photo_url ? (
                              <img src={contact.avatar_url || contact.photo_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                {name.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{contact.email}</p>
                            {(contact.company || contact.job_title) && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                {contact.job_title}{contact.job_title && contact.company ? ' at ' : ''}{contact.company}
                              </p>
                            )}
                          </div>
                        </button>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {contact.is_user && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded">
                              User
                            </span>
                          )}
                          {contact.is_company && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded capitalize">
                              {contact.contact_type || 'Company'}
                            </span>
                          )}
                          {contact.is_person && contact.contact_type && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded capitalize">
                              {contact.contact_type}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-xl">
              <span className="text-sm text-gray-500">
                {selectedContacts.size} selected
              </span>
              <div className="flex items-center gap-2">
                {/* Delete button - only show on personal tab when contacts are selected */}
                {contactPickerTab === 'personal' && selectedContacts.size > 0 && (
                  <button
                    onClick={() => {
                      // Get IDs of selected personal contacts
                      const selectedIds = personalContacts
                        .filter(c => selectedContacts.has(c.email))
                        .map(c => String(c.id));
                      handleDeletePersonalContacts(selectedIds);
                    }}
                    className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowContactPicker(false);
                    setSelectedContacts(new Set());
                    setContactSearch('');
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSelectedContacts}
                  disabled={selectedContacts.size === 0}
                  className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50"
                  style={{ backgroundColor: headerBgColor }}
                >
                  Add Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Send Modal */}
      {showScheduler && (
        <ScheduleSendModal
          headerBgColor={headerBgColor}
          scheduledAt={instance.scheduledAt}
          onSchedule={(date) => {
            onUpdate({ scheduledAt: date, isDirty: true });
            setShowScheduler(false);
          }}
          onCancel={() => setShowScheduler(false)}
        />
      )}

      {/* Meeting Creator Modal */}
      {showMeetingCreator && (
        <MeetingInviteModal
          headerBgColor={headerBgColor}
          onAdd={handleAddMeetingInvite}
          onCancel={() => setShowMeetingCreator(false)}
        />
      )}
    </div>
  );

  return (
    <TotalConnectAccessGate featureName="Email">
      {isSlideSheet ? (
        <div className="absolute inset-0 z-[95] flex min-h-0">
          <button
            type="button"
            className="hidden min-h-0 flex-1 border-0 bg-black/30 p-0 dark:bg-black/40 md:block"
            onClick={handleClose}
            aria-label="Close composer"
          />
          {composerFrame}
        </div>
      ) : (
        <div
          className={cn(
            'fixed inset-0 z-[130] flex items-center justify-center',
            isMobile ? 'bg-black/50 p-0' : 'bg-black/30 p-4',
          )}
        >
          {composerFrame}
        </div>
      )}
    </TotalConnectAccessGate>
  );
}

// Schedule Send Modal Component
function ScheduleSendModal({
  headerBgColor,
  scheduledAt,
  onSchedule,
  onCancel,
}: {
  headerBgColor: string;
  scheduledAt?: Date | null;
  onSchedule: (date: Date | null) => void;
  onCancel: () => void;
}) {
  const [date, setDate] = useState(scheduledAt ? new Date(scheduledAt).toISOString().split('T')[0] : '');
  const [time, setTime] = useState(scheduledAt ? new Date(scheduledAt).toTimeString().slice(0, 5) : '09:00');

  const handleSchedule = () => {
    if (date && time) {
      const scheduled = new Date(`${date}T${time}`);
      if (scheduled > new Date()) {
        onSchedule(scheduled);
      } else {
        notify.error('Please select a future date and time');
      }
    }
  };

  const isMobileApp = isCapacitor();

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120] p-4"
      style={isMobileApp ? {
        top: 'var(--safe-area-inset-top, 0px)',
        bottom: 'var(--safe-area-inset-bottom, 0px)'
      } : undefined}
    >
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-sm">
        <div
          className="flex items-center justify-between px-4 py-3 border-b rounded-t-xl"
          style={{ backgroundColor: headerBgColor }}
        >
          <h3 className="font-medium text-white flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Schedule Send
          </h3>
          <button onClick={onCancel} className="p-1.5 text-white/80 hover:text-white rounded-lg hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>

          {/* Quick options */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                setDate(tomorrow.toISOString().split('T')[0]);
                setTime('08:00');
              }}
              className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Tomorrow 8 AM
            </button>
            <button
              onClick={() => {
                const now = new Date();
                const daysUntilMonday = (8 - now.getDay()) % 7 || 7;
                const monday = new Date();
                monday.setDate(now.getDate() + daysUntilMonday);
                setDate(monday.toISOString().split('T')[0]);
                setTime('08:00');
              }}
              className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Monday 8 AM
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-xl">
          {scheduledAt && (
            <button
              onClick={() => onSchedule(null)}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Remove Schedule
            </button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button
              onClick={handleSchedule}
              disabled={!date || !time}
              className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: headerBgColor }}
            >
              Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Meeting Invite Modal Component
function MeetingInviteModal({
  headerBgColor,
  onAdd,
  onCancel,
}: {
  headerBgColor: string;
  onAdd: (meeting: NonNullable<ComposerInstance['meetingInvite']>) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('11:00');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');

  const handleAdd = () => {
    if (!title || !date || !startTime || !endTime) {
      notify.error('Please fill in all required fields');
      return;
    }

    const startDateTime = new Date(`${date}T${startTime}`);
    const endDateTime = new Date(`${date}T${endTime}`);

    if (endDateTime <= startDateTime) {
      notify.error('End time must be after start time');
      return;
    }

    onAdd({
      title,
      startTime: startDateTime,
      endTime: endDateTime,
      location: location || undefined,
      description: description || undefined,
    });
  };

  const isMobileApp = isCapacitor();

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120] p-4"
      style={isMobileApp ? {
        top: 'var(--safe-area-inset-top, 0px)',
        bottom: 'var(--safe-area-inset-bottom, 0px)'
      } : undefined}
    >
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md">
        <div
          className="flex items-center justify-between px-4 py-3 border-b rounded-t-xl"
          style={{ backgroundColor: headerBgColor }}
        >
          <h3 className="font-medium text-white flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Add Meeting Invite
          </h3>
          <button onClick={onCancel} className="p-1.5 text-white/80 hover:text-white rounded-lg hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Meeting Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Team Sync Meeting"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Conference Room A / Video Call Link"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Meeting agenda and notes..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-xl">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!title || !date || !startTime || !endTime}
            className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50"
            style={{ backgroundColor: headerBgColor }}
          >
            Add Invite
          </button>
        </div>
      </div>
    </div>
  );
}

// Minimized Tab Component
function MinimizedTab({
  instance,
  onClick,
  onClose,
}: {
  instance: ComposerInstance;
  onClick: () => void;
  onClose: () => void;
}) {
  const { clientBranding, primaryColor } = useTheme();
  const headerBgColor = clientBranding?.primary_color || primaryColor || '#3B82F6';
  const title = instance.subject || instance.to || 'New Email';

  return (
    <div
      className="flex items-center gap-1 rounded-t-lg shadow-lg overflow-hidden max-w-[180px] sm:max-w-[220px] touch-manipulation"
      style={{ backgroundColor: headerBgColor }}
    >
      <button
        onClick={onClick}
        className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors"
        title={title}
      >
        <Maximize2 className="w-3.5 h-3.5 text-white/80" />
        <span className="text-sm text-white truncate max-w-[140px]">{title}</span>
        {instance.isDirty && <span className="w-1.5 h-1.5 bg-orange-400 rounded-full" />}
        {instance.scheduledAt && <Clock className="w-3 h-3 text-white/80" />}
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="p-2 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
        title="Close"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Main Multi-Compose Manager Component
export default function MultiComposeManager({
  composers,
  activeComposerId,
  availableFromAccounts = [],
  onUpdateComposer,
  onCloseComposer,
  onMinimizeComposer,
  onRestoreComposer,
  onMaximizeComposer,
  onSent,
  onBackToThreadList,
  composePresentation = 'centered',
  showComposerMaximize = true,
}: MultiComposeManagerProps) {
  const { client } = useAuth();
  const portalTargetRef = useRef<HTMLElement | null>(null);

  if (!portalTargetRef.current && typeof document !== 'undefined') {
    portalTargetRef.current = document.body;
  }

  useEffect(() => {
    if (client?.id) {
      void hydrateEmailConnectPrefs(client.id);
    }
  }, [client?.id]);

  const persistAndCloseComposer = useCallback(
    async (id: string) => {
      const inst = composers.find((c) => c.id === id);
      if (inst) {
        const htmlBody = inst.body;
        const textBody = inst.bodyText;
        if (composerHasPersistableContent(inst, htmlBody, textBody)) {
          await persistComposerDraftToStorageAndOfflineDb({
            instance: inst,
            htmlBody,
            textBody,
            availableFromAccounts,
            onUpdate: (u) => onUpdateComposer(id, u),
          });
        }
      }
      onCloseComposer(id);
    },
    [composers, availableFromAccounts, onUpdateComposer, onCloseComposer],
  );

  // Load client banner settings once on mount for buildSecurityBannerHtml
  useEffect(() => {
    if (!_cachedBannerSettings) {
      fetchEmailSecuritySettings().then(s => {
        _cachedBannerSettings = s.banners;
      }).catch(() => { });
    }
  }, []);

  // Get minimized composers
  const minimizedComposers = composers.filter(c => c.isMinimized);
  const activeComposer = composers.find(c => c.id === activeComposerId && !c.isMinimized);

  const overlay = (
    <>
      {/* Active Composer Window */}
      {activeComposer && (
        <ComposerWindow
          instance={activeComposer}
          isActive={true}
          availableFromAccounts={availableFromAccounts}
          composePresentation={composePresentation}
          showComposerMaximize={showComposerMaximize}
          onUpdate={(updates) => onUpdateComposer(activeComposer.id, updates)}
          onClose={() => onCloseComposer(activeComposer.id)}
          onMinimize={() => onMinimizeComposer(activeComposer.id)}
          onMaximize={() => onMaximizeComposer(activeComposer.id)}
          onSent={(sentEmail) => onSent(activeComposer.id, sentEmail)}
          onBackToThreadList={onBackToThreadList}
        />
      )}

      {/* Minimized Tabs Bar */}
      {minimizedComposers.length > 0 && (
        <div className="fixed bottom-0 right-4 z-[125] flex items-end gap-2">
          {minimizedComposers.map((composer) => (
            <MinimizedTab
              key={composer.id}
              instance={composer}
              onClick={() => onRestoreComposer(composer.id)}
              onClose={() => {
                void persistAndCloseComposer(composer.id);
              }}
            />
          ))}
        </div>
      )}
    </>
  );

  if (!portalTargetRef.current) {
    return null;
  }

  return createPortal(overlay, portalTargetRef.current);
}

// Hook for managing multi-compose state
export function useMultiCompose() {
  const [composers, setComposers] = useState<ComposerInstance[]>([]);
  const [activeComposerId, setActiveComposerId] = useState<string | null>(null);

  // Drafts remain persisted in localStorage / IndexedDB and are accessible
  // via the Drafts folder. They are NOT restored as floating minimized
  // composers on mount — floating buttons only exist for the compose
  // session in which they were created.
  useEffect(() => {
    // No-op: intentionally do not restore drafts as minimized composers.
  }, []);

  const createComposer = useCallback((replyTo?: ComposerInstance['replyTo'] & { isDraft?: boolean }, fromAccount?: FromAccount | null) => {
    if (composers.length >= MAX_COMPOSERS) {
      notify.warning(`Maximum ${MAX_COMPOSERS} compose windows allowed`);
      return null;
    }

    // Minimize current active composer
    if (activeComposerId) {
      setComposers(prev => prev.map(c =>
        c.id === activeComposerId ? { ...c, isMinimized: true } : c
      ));
    }

    const newId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? `compose-${crypto.randomUUID()}`
        : `compose-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    // Check if this is editing a draft (not a reply/forward)
    const isDraft = replyTo?.isDraft === true;
    const draftEmail = isDraft ? replyTo?.email : null;

    // Helper to extract email address from EmailAddress type or string
    const formatRecipients = (recipients: unknown): string => {
      if (!recipients) return '';
      if (typeof recipients === 'string') return recipients;
      if (Array.isArray(recipients)) {
        return recipients
          .map((r) => {
            if (typeof r === 'string') return r;
            if (r && typeof r === 'object') {
              const o = r as Record<string, unknown>;
              if (typeof o.address === 'string') return o.address;
              if (typeof o.email === 'string') return o.email;
            }
            return '';
          })
          .filter(Boolean)
          .join(', ');
      }
      if (typeof recipients === 'object') {
        const o = recipients as Record<string, unknown>;
        if (typeof o.address === 'string') return o.address;
        if (typeof o.email === 'string') return o.email;
      }
      return '';
    };

    const newComposer: ComposerInstance = {
      id: newId,
      replyTo: isDraft ? null : (replyTo || null), // Don't set replyTo for drafts
      // For drafts, pre-populate with draft content
      to: draftEmail ? formatRecipients(draftEmail.to) : '',
      cc: draftEmail ? formatRecipients(draftEmail.cc) : '',
      bcc: draftEmail ? formatRecipients(draftEmail.bcc) : '',
      subject: draftEmail?.subject || '',
      body: draftEmail?.html || draftEmail?.body || '',
      bodyText: draftEmail?.text || '',
      attachments: [],
      forwardedAttachments: draftEmail?.attachments || undefined,
      isMinimized: false,
      isMaximized: false,
      createdAt: Date.now(),
      lastSavedAt: 0,
      isDirty: false,
      // Track the original draft messageId for cleanup after sending
      draftMessageId: draftEmail?.messageId || null,
      // Pre-select from account if provided (e.g., when composing from shared mailbox)
      fromAccount: fromAccount || null,
      sendAs: fromAccount?.isShared ? true : undefined,
    };

    setComposers(prev => [...prev, newComposer]);
    setActiveComposerId(newId);
    return newId;
  }, [composers.length, activeComposerId]);

  const updateComposer = useCallback((id: string, updates: Partial<ComposerInstance>) => {
    setComposers(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  }, []);

  const closeComposer = useCallback((id: string) => {
    setComposers((prev) => {
      const target = prev.find((c) => c.id === id);
      if (target) {
        const html = target.body;
        const text = target.bodyText;
        if (!composerHasPersistableContent(target, html, text)) {
          const drafts = readStoredDrafts();
          delete drafts[id];
          writeStoredDrafts(drafts);
          void (async () => {
            try {
              const { offlineEmail } = await import('@/lib/offlineEmail');
              await offlineEmail.deleteDraft(target.draftMessageId || target.id);
            } catch {
              /* ignore */
            }
          })();
        }
      }
      return prev.filter((c) => c.id !== id);
    });
    if (activeComposerId === id) {
      setActiveComposerId(null);
    }
  }, [activeComposerId]);

  const minimizeComposer = useCallback((id: string) => {
    setComposers(prev => prev.map(c => c.id === id ? { ...c, isMinimized: true } : c));
    setActiveComposerId(null);
  }, []);

  const restoreComposer = useCallback((id: string) => {
    // Minimize current active
    if (activeComposerId && activeComposerId !== id) {
      setComposers(prev => prev.map(c =>
        c.id === activeComposerId ? { ...c, isMinimized: true } : c
      ));
    }

    setComposers(prev => prev.map(c => c.id === id ? { ...c, isMinimized: false } : c));
    setActiveComposerId(id);
  }, [activeComposerId]);

  const maximizeComposer = useCallback((id: string) => {
    setComposers(prev => prev.map(c =>
      c.id === id ? { ...c, isMaximized: !c.isMaximized } : c
    ));
  }, []);

  const handleSent = useCallback((id: string) => {
    closeComposer(id);
  }, [closeComposer]);

  return {
    composers,
    activeComposerId,
    createComposer,
    updateComposer,
    closeComposer,
    minimizeComposer,
    restoreComposer,
    maximizeComposer,
    handleSent,
  };
}
